// src/views/LoanDetail.jsx
// Extracted from App.jsx — behavior-preserving split (2026-07-20).
// 10/2/26: Max's own files (access.ownFilesOnly — src/access.js) open here
// too, with full tracking — stage, notes, a follow-up date + tasks card,
// documents he uploads himself — and NOTHING that reaches the borrower portal
// (access.portal) or a mail-list lead (access.leads: no do-not-contact mirror
// onto the source lead). He can't reassign a file either.

import React, { useState, useEffect, useRef } from "react";
import { supabase, isSupabaseReady, STAFF } from '../supabase';
import * as BorrowerAPI from '../borrowerApi';
import { C, Ic } from '../theme';
import { Badge, Btn, CallBtn, Hdr, Inp, MoneyInp, Sel } from '../ui';
import { CALL_CLICKS_MAX, DM_CAMPAIGNS, FALLOUT_NOAPP, FALLOUT_QUAL, FALLOUT_REASONS, FALLOUT_STAGES, FALLOUT_TAGS, LENDER_CHECKLISTS, LOCK_STAGE, MANUAL_DEAL_LABEL, HS2_LOAN_TYPE, STAGES, STC } from '../constants';
import { ageFromDobStr, baToDb, buildDocSections, defaultDocRequest, fetchLeadById, fmtDob, hashPassword, isDueBy, isHs2File, isoDatePlus, loanAge, ownerOf, parseDateLocal, safeStorageName, stageAtOrPast, stampLeadOwner, uploadReceiptFile } from '../lib';
import { OwnerChip } from './Dashboard';
import { useOwnershipUi } from '../features';
import { InterestMenu } from './Interest';
import { applyInterest, interestNameStyle, interestStyle } from '../interest';
import { cltvText, payCents } from '../calcRules';
import { staffAccess, isPracticeOrUnknown, usePractice } from '../access';
import { REIMB_KINDS, addItem, fmtDay, fmtMoney, liveItems, markReimbursed, newReimbId, owedItems, owedTotal, reimbursedTotal, reimbursementPath, removeItem, todayLocal, toAmount, updateItem } from '../reimbursements';

// Picking this reason without actually marking the person do-not-contact is
// how a compliance problem starts — same rule as the New Call console.
const DNC_REASON_RE=/removed from mailings|do not contact/i;

// Collapsible card — the loan page shows a snapshot up top and keeps the
// rarely-needed panels (details, checklist, portal, docs) one click away.
function Section({id,title,color,summary,open,onToggle,children}){
  return(<div id={id} style={{background:C.card,borderRadius:14,border:"1px solid "+C.border,marginBottom:10,overflow:"hidden",scrollMarginTop:90}}>
    <button onClick={onToggle} aria-expanded={!!open} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px 20px",background:"transparent",border:"none",cursor:"pointer",textAlign:"left",fontFamily:"'Mulish',system-ui,sans-serif"}}>
      <span style={{fontSize:13.5,fontWeight:800,color:color||C.text,flexShrink:0}}>{title}</span>
      <span style={{fontSize:12,color:C.textDim,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{summary||""}</span>
      <span style={{fontSize:16,color:C.textDim,lineHeight:1,transform:open?"rotate(90deg)":"none",transition:"transform .15s",flexShrink:0}}>›</span>
    </button>
    {open&&<div style={{padding:"4px 20px 20px",borderTop:"1px solid "+C.border}}>{children}</div>}
  </div>);
}

// ═══════ POST-CLOSING & EPO TRACKER ═══════
// Every field here persists as it is typed (upd → the debounced App autosave);
// the checklist / funded date / EPO record ride in follow_ups._wf via wfPack.
//
// Auto-Funded (Reece, 10/10/26: "if I check the box on things like loan
// funded, wire received all that stuff, automatically move the deal to funded
// status"): ticking one of the boxes that mean money moved, or entering a
// Funding Date, moves a Closed file to Funded through the page's changeStage
// (the same path as the Stage select — activity-log line included). Unticking
// never moves it back. Signed Docs Returned and Rescission Expired come
// before funding; File Archived and Google Review Requested (he loves that
// one — keep it) never touch the stage.
const FUNDED_BOXES={postClose_wireReceived:1,postClose_payoffSent:1,postClose_borrowerNotified:1,postClose_commReceived:1};
function PostClosingTracker({loan,upd,$,changeStage}){
  // "Moved to Funded — <why>" after this page moved it — only while the file
  // IS Funded: moved back (the header select / Details → Stage) it goes, and
  // it doesn't come back if the file is later set to Funded by hand.
  const [moved,sMoved]=useState("");
  useEffect(function(){if(loan.stage!=="Funded")sMoved("");},[loan.stage]);
  const autoFund=function(why){
    if(!changeStage||loan.stage==="Funded")return;
    changeStage("Funded",why);
    sMoved(why);
  };
  // The Funding Date field is what "sets the EPO clock", so it wins; the
  // wire-received tick is only the fallback (it also seeds fundedDate).
  const fundDateStr=loan.fundedDate||loan.postClose_wireReceived||"";

  // Calendar-day math at LOCAL midnight ("M/D/YYYY" or "YYYY-MM-DD"), so the
  // clock flips on the right day in Pacific and Eastern alike.
  const parseDay=function(s){
    if(!s)return null;
    var d=parseDateLocal(s);
    if(!d){var dt=new Date(s);if(isNaN(dt.getTime()))return null;d=dt;}
    return new Date(d.getFullYear(),d.getMonth(),d.getDate());
  };
  const DAY=24*60*60*1000;
  var now=new Date();
  var today=new Date(now.getFullYear(),now.getMonth(),now.getDate());
  var fundDate=parseDay(fundDateStr);
  var epoSafeDate=fundDate?new Date(fundDate.getFullYear(),fundDate.getMonth(),fundDate.getDate()+183):null; // ~6 months (183 days)
  var daysElapsed=fundDate?Math.round((today-fundDate)/DAY):null;
  var daysRemaining=epoSafeDate?Math.max(0,Math.round((epoSafeDate-today)/DAY)):null;
  var fundIso=fundDate?fundDate.getFullYear()+"-"+String(fundDate.getMonth()+1).padStart(2,"0")+"-"+String(fundDate.getDate()).padStart(2,"0"):"";
  var epoWindowClear=daysRemaining!==null&&daysRemaining===0;
  var epoWindowActive=daysRemaining!==null&&daysRemaining>0;
  var pct=daysElapsed!==null?Math.min(100,Math.round((daysElapsed/183)*100)):0;

  // EPO risk status
  var epoStatus=loan.epo_status||"monitoring"; // "monitoring" | "triggered" | "clear" | "resolved"
  var epoStatusColors={monitoring:C.orange,triggered:C.red,clear:C.green,resolved:C.cyan};
  var epoStatusLabels={monitoring:"Monitoring",triggered:"EPO Triggered",clear:"Window Cleared",resolved:"Resolved"};

  return(<div style={{background:C.card,borderRadius:14,border:"1px solid "+C.cyan+"40",padding:20,marginTop:16}}>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
      <h3 style={{fontSize:14,fontWeight:700,color:C.cyan,margin:0}}>Post-Closing Tracker</h3>
      {loan.stage==="Funded"&&<Badge label="Funded ✓" color={C.cyan} bg={C.cyan+"18"}/>}
      <span style={{fontSize:10.5,color:C.textDim,marginLeft:"auto"}}>Saves as you type</span>
    </div>
    {moved&&loan.stage==="Funded"&&<div data-autofund-note="1" role="status" style={{marginTop:-6,marginBottom:14,fontSize:12,fontWeight:700,color:C.cyan}}>Moved to Funded <span style={{fontWeight:500,color:C.textMuted}}>— {moved}</span></div>}

    {/* ── CHECKLIST ── */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10,marginBottom:20}}>
      {[
        {k:"postClose_docsReturned",l:"Signed Docs Returned to Lender",icon:"📄"},
        {k:"postClose_rescissionExpired",l:"3-Day Rescission Expired",icon:"⏱"},
        {k:"postClose_wireReceived",l:"Wire / Funding Received",icon:"💰"},
        {k:"postClose_payoffSent",l:"Payoff Wired to Servicer",icon:"🏦"},
        {k:"postClose_borrowerNotified",l:"Borrower Funding Notification Sent",icon:"📲"},
        {k:"postClose_commReceived",l:"Commission Received",icon:"✅"},
        {k:"postClose_fileArchived",l:"File Archived",icon:"🗂"},
        {k:"postClose_reviewRequested",l:"Google Review Requested",icon:"⭐"},
      ].map(function(item){
        var done=!!(loan[item.k]);
        // Wire received auto-sets funded date for EPO tracking
        var toggle=function(){
          var newVal=done?"":new Date().toLocaleDateString();
          upd(item.k,newVal);
          if(item.k==="postClose_wireReceived"&&!done&&!loan.fundedDate){upd("fundedDate",newVal);}
          // Money moved → Funded (ticking only; unticking never moves it back).
          if(!done&&FUNDED_BOXES[item.k])autoFund(item.l+" checked");
        };
        return(<div key={item.k} data-postclose-item={item.k} role="checkbox" aria-checked={done} tabIndex={0} onClick={toggle}
          onKeyDown={function(e){if(e.target!==e.currentTarget)return;if(e.key===" "||e.key==="Enter"){e.preventDefault();toggle();}}} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:8,border:"1px solid "+(done?C.cyan+"40":C.border),background:done?C.cyan+"08":"transparent",cursor:"pointer",transition:"all 0.1s"}} onMouseEnter={function(e){if(!done)e.currentTarget.style.background=C.surfaceAlt;}} onMouseLeave={function(e){e.currentTarget.style.background=done?C.cyan+"08":"transparent";}}>
          <div style={{width:20,height:20,borderRadius:4,border:"2px solid "+(done?C.cyan:C.borderLight),background:done?C.cyan:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {done&&<span style={{color:"#fff",fontSize:10}}>{Ic.chk}</span>}
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:12,fontWeight:done?600:500,color:done?C.cyan:C.text}}>{item.icon} {item.l}</div>
            {done&&loan[item.k]&&<div style={{fontSize:10,color:C.textDim,marginTop:1}}>{loan[item.k]}</div>}
          </div>
        </div>);
      })}
    </div>

    {/* ── EPO / CHARGEBACK WINDOW ── */}
    <div style={{borderTop:"1px solid "+C.border,paddingTop:18,marginBottom:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:16}}>⚠️</span>
          <h4 style={{fontSize:14,fontWeight:700,color:C.orange,margin:0}}>Early Payoff (EPO) Chargeback Tracker</h4>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,color:epoStatusColors[epoStatus],background:epoStatusColors[epoStatus]+"18"}}>{epoStatusLabels[epoStatus]}</span>
          <select value={epoStatus} onChange={function(e){upd("epo_status",e.target.value);}} style={{padding:"5px 10px",borderRadius:6,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:12,outline:"none",fontFamily:"'Mulish',system-ui,sans-serif"}}>
            <option value="monitoring">Monitoring</option>
            <option value="triggered">EPO Triggered</option>
            <option value="clear">Window Cleared</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      <div style={{fontSize:12,color:C.textMuted,marginBottom:14,lineHeight:1.6,padding:"10px 14px",background:C.surfaceAlt,borderRadius:8,borderLeft:"3px solid "+C.orange}}>
        Most lenders require commission repayment if the borrower pays off or cancels the loan within <strong style={{color:C.text}}>6 months of funding</strong>. This tracker monitors that window and flags when you're in the clear. Check your lender's specific agreement for the exact recapture terms — FOA and MoO both enforce 6-month EPO clawback provisions on lender-paid compensation.
      </div>

      {/* Funding date input */}
      <div style={{display:"flex",gap:12,alignItems:"flex-end",marginBottom:16,flexWrap:"wrap"}}>
        <div style={{flex:"1 1 200px"}}>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:C.textDim,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Funding Date <span style={{color:C.accent,fontWeight:400}}>— sets EPO clock</span></label>
          <input type="date" data-funding-date="1" value={loan.fundedDate?fundIso:""}
            onChange={function(e){
              if(!e.target.value){upd("fundedDate","");return;}   // clearing it never moves the file back
              var d=parseDateLocal(e.target.value);if(!d)return;
              upd("fundedDate",(d.getMonth()+1)+"/"+d.getDate()+"/"+d.getFullYear());
              autoFund("Funding Date entered");
            }}
            style={{width:"100%",padding:"10px 12px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:13,outline:"none",fontFamily:"'Mulish',system-ui,sans-serif",colorScheme:"light",boxSizing:"border-box"}}/>
        </div>
        {epoSafeDate&&<div style={{flex:"1 1 200px",padding:"10px 14px",background:C.surfaceAlt,borderRadius:8,border:"1px solid "+C.border}}>
          <div style={{fontSize:10,color:C.textDim,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>EPO-Safe Date</div>
          <div style={{fontSize:14,fontWeight:700,color:epoWindowClear?C.green:C.orange,fontFamily:"'Mulish',system-ui,sans-serif"}}>
            {(epoSafeDate.getMonth()+1)+"/"+(epoSafeDate.getDate())+"/"+epoSafeDate.getFullYear()}
          </div>
          <div style={{fontSize:10,color:C.textDim,marginTop:2}}>183 days from funding</div>
        </div>}
      </div>

      {/* Progress bar */}
      {fundDate&&<div style={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <span style={{fontSize:11,color:C.textDim}}>EPO Window Progress</span>
          <span style={{fontSize:12,fontWeight:700,color:epoWindowClear?C.green:epoWindowActive?C.orange:C.textDim,fontFamily:"'Mulish',system-ui,sans-serif"}}>
            {epoWindowClear?"✓ CLEAR — commission secured"
             :epoWindowActive?daysRemaining+" days remaining"
             :"Set funding date to start clock"}
          </span>
        </div>
        <div style={{height:12,background:C.surfaceAlt,borderRadius:6,overflow:"hidden",border:"1px solid "+C.border}}>
          <div style={{height:"100%",width:pct+"%",background:epoWindowClear?"linear-gradient(90deg,"+C.green+",#68D391)":"linear-gradient(90deg,"+C.orange+","+C.gold+")",borderRadius:6,transition:"width 0.4s",position:"relative"}}>
            {pct>15&&<span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",fontSize:9,fontWeight:700,color:"#fff"}}>{pct}%</span>}
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",marginTop:4,fontSize:10,color:C.textDim}}>
          <span>Funded: {fundDateStr}</span>
          <span>{epoWindowClear?"183 / 183 days":(daysElapsed||0)+" / 183 days elapsed"}</span>
        </div>
      </div>}

      {/* Status banners */}
      {epoStatus==="triggered"&&<div style={{padding:"14px 16px",background:C.redBg,borderRadius:10,border:"1px solid "+C.red+"40",marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:700,color:C.red,marginBottom:4}}>⚠️ EPO Triggered — Potential Chargeback</div>
        <div style={{fontSize:12,color:C.textMuted,lineHeight:1.6,marginBottom:10}}>The borrower has paid off or cancelled within the 6-month window. Review your lender agreement to confirm whether a chargeback applies and the amount owed.</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:C.textDim,marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>EPO Date</label>
            <input type="date" value={loan.epo_date||""} onChange={function(e){upd("epo_date",e.target.value);}} style={{padding:"8px 12px",borderRadius:6,border:"1px solid "+C.red+"40",background:C.surfaceAlt,color:C.text,fontSize:12,outline:"none",fontFamily:"'Mulish',system-ui,sans-serif",colorScheme:"light"}}/>
          </div>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:C.textDim,marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>Chargeback Amount (if confirmed)</label>
            <input type="text" inputMode="numeric" placeholder="$0" value={loan.epo_chargebackAmt||""} onChange={function(e){upd("epo_chargebackAmt",e.target.value.replace(/[^0-9]/g,""));}} style={{padding:"8px 12px",borderRadius:6,border:"1px solid "+C.red+"40",background:C.surfaceAlt,color:C.red,fontSize:13,outline:"none",fontFamily:"'Mulish',system-ui,sans-serif",width:140}}/>
          </div>
          <div>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:C.textDim,marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>Notes / Resolution</label>
            <textarea placeholder="Lender confirmation, dispute notes, payment status..." value={loan.epo_notes||""} onChange={function(e){upd("epo_notes",e.target.value);}} style={{width:"100%",minHeight:60,padding:"8px 12px",borderRadius:6,border:"1px solid "+C.red+"30",background:C.surfaceAlt,color:C.text,fontSize:12,fontFamily:"'Mulish',system-ui,sans-serif",resize:"vertical",outline:"none",boxSizing:"border-box"}}/>
          </div>
        </div>
      </div>}

      {epoWindowClear&&epoStatus!=="triggered"&&epoStatus!=="resolved"&&<div style={{padding:"12px 16px",background:C.greenBg,borderRadius:10,border:"1px solid "+C.green+"40",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
        <span style={{color:C.green,fontSize:18}}>{Ic.chk}</span>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:C.green}}>6-Month Window Cleared</div>
          <div style={{fontSize:12,color:C.textMuted}}>Commission is secured — no chargeback obligation remains for this file.</div>
        </div>
      </div>}

      {epoStatus==="resolved"&&<div style={{padding:"12px 16px",background:C.cyan+"10",borderRadius:10,border:"1px solid "+C.cyan+"40",marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:700,color:C.cyan}}>Resolved</div>
        <div style={{fontSize:12,color:C.textMuted}}>EPO issue has been resolved. See notes below.</div>
        {loan.epo_chargebackAmt&&<div style={{fontSize:12,color:C.textDim,marginTop:4}}>Chargeback recorded: <strong style={{color:C.text}}>${parseInt(loan.epo_chargebackAmt||0).toLocaleString()}</strong></div>}
      </div>}
    </div>

    {/* ── POST-CLOSING NOTES ── */}
    <div style={{borderTop:"1px solid "+C.border,paddingTop:16}}>
      <label style={{display:"block",fontSize:10,fontWeight:700,color:C.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:.5}}>Post-Closing Notes</label>
      <textarea placeholder="Wire confirmation number, servicer info, borrower feedback, chargeback correspondence, etc." value={loan.postCloseNotes||""} onChange={function(e){upd("postCloseNotes",e.target.value);}} style={{width:"100%",minHeight:60,padding:"10px 14px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:12,fontFamily:"'Mulish',system-ui,sans-serif",resize:"vertical",outline:"none",boxSizing:"border-box"}}/>
    </div>
  </div>);
}

// ═══════ REIMBURSEMENTS OWED TO YOU (10/10/26) ═══════
// Reece: "make a note that I need to be reimbursed for credit report, or
// appraisal, or things like that". On every file, any stage. The list is
// loan.reimbursements (src/reimbursements.js) in follow_ups._wf — admin-only,
// never in what the borrower portal gets. Delete is a soft delete (deletedAt)
// so a stale tab can neither bring it back nor lose it. An invoice goes to the
// loan-documents bucket at <loanId>/reimbursements/<item id>-<safe name>
// (uploadReceiptFile: never overwrites) and opens through the same signed-URL
// viewer as the file's documents. It is NOT added to the file's uploads (the
// portal lists those).
const RB_LBL={display:"block",fontSize:10,fontWeight:700,color:C.textDim,marginBottom:5,textTransform:"uppercase",letterSpacing:.5};
const RB_SEL={width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:13,outline:"none",fontFamily:"'Mulish',system-ui,sans-serif",minHeight:38,boxSizing:"border-box"};
const RB_INP={padding:"8px 12px",fontSize:13,minHeight:38};
function rbBlank(){return{kind:REIMB_KINDS[0],amount:"",paidOn:todayLocal(),note:"",file:null};}
// onWrite: called on every change — Loan Detail pins the section open, so
// marking the last item reimbursed doesn't fold it away under his cursor.
function ReimbursementsPanel({loan,setLoans,me,practice,viewDoc,onWrite}){
  const [form,sForm]=useState(rbBlank);
  const [editId,sEditId]=useState(null);
  const [edit,sEdit]=useState(null);
  const [busy,sBusy]=useState(false);
  const [msg,sMsg]=useState("");
  const addFileRef=useRef(null);
  const editFileRef=useRef(null);
  const items=liveItems(loan);
  const owed=items.filter(function(x){return!x.reimbursedOn;});
  const paid=items.filter(function(x){return!!x.reimbursedOn;}).sort(function(a,b){return String(b.reimbursedOn).localeCompare(String(a.reimbursedOn));});
  const owedSum=owedTotal(loan);
  const paidSum=reimbursedTotal(loan);
  const what=function(x){return x.kind+" "+fmtMoney(x.amount);};

  // One write to the list on file `loanId` (+ an optional activity line —
  // activity_log is admin-only too). fn returns the same array when nothing
  // changed, and then nothing is written.
  const writeList=function(loanId,fn,logText){
    var ts=new Date();
    if(onWrite)onWrite();
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loanId)return l;
      var cur=Array.isArray(l.reimbursements)?l.reimbursements:[];
      var next=fn(cur);
      if(next===cur)return l;
      var n=Object.assign({},l,{reimbursements:next});
      if(logText)n.activityLog=(l.activityLog||[]).concat([{text:logText,type:"system",timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),epoch:ts.getTime()}]);
      if(!n.owner)n.owner=me;
      return n;
    });});
  };
  // → {fileName, filePath} or {error}. Practice mode / no cloud: nothing goes up.
  const uploadInvoice=async function(loanId,itemId,file){
    if(practice||isPracticeOrUnknown(me))return{error:"Practice mode — the invoice isn't uploaded"};
    if(!isSupabaseReady())return{error:"attaching an invoice needs the cloud connection"};
    var path=reimbursementPath(loanId,itemId,safeStorageName(file.name,"invoice"));
    var r=await uploadReceiptFile(path,file,{contentType:file.type||undefined});
    if(!r||r.error||!r.path)return{error:(r&&r.error&&(r.error.message||String(r.error)))||"upload failed"};
    return{fileName:String(file.name||"invoice"),filePath:r.path};
  };
  const attach=async function(loanId,itemId,file,doneMsg){
    sBusy(true);
    var up=await uploadInvoice(loanId,itemId,file);
    sBusy(false);
    if(up.error){sMsg("Saved — but the invoice didn't upload ("+up.error+"). Attach it again with Edit.");return;}
    writeList(loanId,function(list){return updateItem(list,itemId,{fileName:up.fileName,filePath:up.filePath});});
    sMsg(doneMsg);
  };

  const add=function(){
    if(busy)return;
    var amt=toAmount(form.amount);
    if(!(amt>0)){sMsg("Enter the amount you paid.");return;}
    var loanId=loan.id;
    var id=newReimbId(loan.reimbursements);
    var paidOn=form.paidOn||todayLocal();
    var file=form.file;
    var fields={id:id,kind:form.kind,amount:amt,paidOn:paidOn,note:form.note};
    writeList(loanId,function(list){return addItem(list,fields);},"Reimbursement owed to you: "+form.kind+" "+fmtMoney(amt)+" — paid "+fmtDay(paidOn)+(form.note.trim()?" · "+form.note.trim():""));
    sForm(rbBlank());
    if(addFileRef.current)addFileRef.current.value="";
    if(file){sMsg("Saved — uploading the invoice…");attach(loanId,id,file,"✓ Saved with the invoice");}
    else sMsg("✓ Saved");
  };
  const mark=function(x){writeList(loan.id,function(list){return markReimbursed(list,x.id);},"Reimbursed: "+what(x));sMsg("");};
  const undo=function(x){writeList(loan.id,function(list){return markReimbursed(list,x.id,null);},"Reimbursement owed again (un-marked): "+what(x));sMsg("");};
  const del=function(x){
    if(!window.confirm("Delete this reimbursement — "+what(x)+" paid "+fmtDay(x.paidOn)+"?"))return;
    writeList(loan.id,function(list){return removeItem(list,x.id);},"Reimbursement deleted: "+what(x));
    if(editId===x.id){sEditId(null);sEdit(null);}
    sMsg("");
  };
  const startEdit=function(x){sEditId(x.id);sEdit({kind:x.kind,amount:String(x.amount||""),paidOn:x.paidOn||todayLocal(),note:x.note||"",file:null,dropFile:false});sMsg("");};
  const cancelEdit=function(){sEditId(null);sEdit(null);};
  const saveEdit=function(){
    if(!edit||!editId||busy)return;
    var amt=toAmount(edit.amount);
    if(!(amt>0)){sMsg("Enter the amount you paid.");return;}
    var loanId=loan.id,id=editId,file=edit.file;
    var patch={kind:edit.kind,amount:amt,paidOn:edit.paidOn||todayLocal(),note:edit.note};
    if(edit.dropFile&&!file){patch.fileName=null;patch.filePath=null;}
    writeList(loanId,function(list){return updateItem(list,id,patch);});
    sEditId(null);sEdit(null);
    if(file){sMsg("Saved — uploading the invoice…");attach(loanId,id,file,"✓ Saved with the invoice");}
    else sMsg("✓ Saved");
  };

  const fields=function(v,set,fileRef,prefix,hasFile){
    return(<div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
      <label data-reimb-field={prefix+"kind"} style={{display:"block",flex:"1 1 160px",minWidth:150}}><span style={RB_LBL}>Type</span>
        <select value={v.kind} onChange={function(e){var k=e.target.value;set(function(p){return Object.assign({},p,{kind:k});});}} style={RB_SEL}>
          {REIMB_KINDS.concat(REIMB_KINDS.indexOf(v.kind)>=0?[]:[v.kind]).map(function(k){return <option key={k} value={k}>{k}</option>;})}
        </select></label>
      <label data-reimb-field={prefix+"amount"} style={{display:"block",flex:"0 1 130px",minWidth:110}}><span style={RB_LBL}>Amount</span>
        <MoneyInp allowCents value={v.amount} onChange={function(e){var a=e.target.value;set(function(p){return Object.assign({},p,{amount:a});});}} placeholder="0.00" style={RB_INP}/></label>
      <label data-reimb-field={prefix+"paidOn"} style={{display:"block",flex:"0 1 160px",minWidth:140}}><span style={RB_LBL}>Date paid</span>
        <Inp type="date" value={v.paidOn} onChange={function(e){var d=e.target.value;set(function(p){return Object.assign({},p,{paidOn:d});});}} style={Object.assign({},RB_INP,{colorScheme:"light"})}/></label>
      <label data-reimb-field={prefix+"note"} style={{display:"block",flex:"2 1 220px",minWidth:180}}><span style={RB_LBL}>Note</span>
        <Inp value={v.note} onChange={function(e){var t=e.target.value;set(function(p){return Object.assign({},p,{note:t});});}} placeholder="e.g. paid on the Amex — lender reimburses at closing" style={RB_INP}/></label>
      <div style={{display:"flex",alignItems:"center",gap:8,flex:"1 1 100%",flexWrap:"wrap"}}>
        <input ref={fileRef} type="file" accept="application/pdf,image/*" data-reimb-field={prefix+"file"} style={{display:"none"}}
          onChange={function(e){var fl=e.target.files&&e.target.files[0];set(function(p){return Object.assign({},p,{file:fl||null});});}}/>
        <Btn small onClick={function(){if(fileRef.current)fileRef.current.click();}} style={{fontSize:11}}>{v.file?"Change invoice":hasFile?"Replace invoice":"Attach invoice (optional)"}</Btn>
        {v.file&&<span style={{fontSize:11.5,color:C.text,fontWeight:600}}>{v.file.name}</span>}
        {v.file&&<button onClick={function(){if(fileRef.current)fileRef.current.value="";set(function(p){return Object.assign({},p,{file:null});});}} style={{background:"none",border:"none",color:C.textDim,cursor:"pointer",fontSize:11,fontFamily:"'Mulish',system-ui,sans-serif"}}>remove</button>}
      </div>
    </div>);
  };

  const row=function(x){
    var isPaid=!!x.reimbursedOn;
    if(editId===x.id&&edit)return(<div key={x.id} data-reimb-item={x.id} data-reimb-editing="1" style={{padding:"12px 14px",borderRadius:10,border:"1px solid "+C.clay+"60",background:C.surfaceAlt}}>
      {fields(edit,sEdit,editFileRef,"edit-",!!x.filePath&&!edit.dropFile)}
      {x.filePath&&!edit.file&&<div style={{marginTop:8,fontSize:11.5,color:C.textMuted}}>
        {edit.dropFile?<span>Invoice will be removed from this item. <button onClick={function(){sEdit(function(p){return Object.assign({},p,{dropFile:false});});}} style={{background:"none",border:"none",color:C.accent,cursor:"pointer",fontSize:11.5,padding:0,fontFamily:"'Mulish',system-ui,sans-serif"}}>Keep it</button></span>
        :<span>Invoice on file: <strong style={{color:C.text}}>{x.fileName||"invoice"}</strong> · <button onClick={function(){sEdit(function(p){return Object.assign({},p,{dropFile:true});});}} style={{background:"none",border:"none",color:C.red,cursor:"pointer",fontSize:11.5,padding:0,fontFamily:"'Mulish',system-ui,sans-serif"}}>Remove from this item</button></span>}
      </div>}
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:10}}>
        <Btn small onClick={cancelEdit}>Cancel</Btn>
        <Btn small primary onClick={saveEdit} disabled={busy}>Save</Btn>
      </div>
    </div>);
    return(<div key={x.id} data-reimb-item={x.id} data-reimb-status={isPaid?"reimbursed":"owed"} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:10,background:isPaid?C.surfaceAlt:C.card,border:"1px solid "+(isPaid?C.border:C.clay+"55"),flexWrap:"wrap",opacity:isPaid?.8:1}}>
      <div style={{flex:"1 1 220px",minWidth:0}}>
        <div style={{display:"flex",alignItems:"baseline",gap:10,flexWrap:"wrap"}}>
          <span style={{fontSize:13,fontWeight:800,color:C.text}}>{x.kind}</span>
          <span style={{fontSize:14,fontWeight:800,color:isPaid?C.textMuted:C.clayDeep}}>{fmtMoney(x.amount)}</span>
          <span style={{fontSize:11.5,color:C.textDim}}>paid {fmtDay(x.paidOn)}</span>
        </div>
        {x.note&&<div style={{fontSize:12,color:C.textMuted,marginTop:2,wordBreak:"break-word"}}>{x.note}</div>}
      </div>
      {x.filePath&&<button data-reimb-view={x.id} onClick={function(){viewDoc({storagePath:x.filePath,name:x.fileName});}} title={"Open "+(x.fileName||"the invoice")} style={{background:"none",border:"none",color:C.accent,cursor:"pointer",fontSize:11.5,fontWeight:700,fontFamily:"'Mulish',system-ui,sans-serif"}}>View invoice</button>}
      {isPaid?<Badge label={"Reimbursed "+fmtDay(x.reimbursedOn)} color={C.feelGood} bg={C.greenBg}/>:<span style={{display:"inline-flex",alignItems:"center",padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:700,color:C.clayDeep,background:C.white,border:"1px solid "+C.clay+"99",whiteSpace:"nowrap"}}>Owed</span>}
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {isPaid?<Btn small onClick={function(){undo(x);}} style={{fontSize:11}}><span data-reimb-undo={x.id}>Undo</span></Btn>
        :<Btn small onClick={function(){mark(x);}} style={{fontSize:11,color:C.green}}><span data-reimb-mark={x.id}>✓ Mark reimbursed</span></Btn>}
        <Btn small onClick={function(){startEdit(x);}} style={{fontSize:11}}><span data-reimb-edit={x.id}>Edit</span></Btn>
        <Btn small onClick={function(){del(x);}} style={{fontSize:11,color:C.red}}><span data-reimb-delete={x.id}>Delete</span></Btn>
      </div>
    </div>);
  };

  return(<div data-reimbursements={loan.id}>
    <div style={{fontSize:11.5,color:C.textDim,margin:"4px 0 12px",lineHeight:1.5}}>Money you paid on this file and are owed back — credit report, appraisal, flood cert… Only you see this; it never goes to the borrower.</div>
    {(owed.length>0||paid.length>0)&&<div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:12,fontSize:12.5,color:C.textMuted}}>
      <span>Owed to you <strong data-reimb-owed-total="1" style={{color:owedSum>0?C.clayDeep:C.textMuted,fontSize:14}}>{fmtMoney(owedSum)}</strong></span>
      {paid.length>0&&<span>Reimbursed <strong style={{color:C.green}}>{fmtMoney(paidSum)}</strong></span>}
    </div>}
    {items.length>0&&<div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
      {owed.map(row)}
      {paid.map(row)}
    </div>}
    <div data-reimb-form="1" style={{padding:"12px 14px",borderRadius:10,border:"1px dashed "+C.border}}>
      <div style={{fontSize:11,fontWeight:800,color:C.text,marginBottom:10}}>Add one</div>
      {fields(form,sForm,addFileRef,"")}
      <div style={{display:"flex",alignItems:"center",gap:10,marginTop:10,flexWrap:"wrap"}}>
        <Btn small primary onClick={add} disabled={busy}><span data-reimb-add="1">+ Add reimbursement</span></Btn>
        {msg&&<span role="status" data-reimb-msg="1" style={{fontSize:11.5,fontWeight:600,color:/^✓/.test(msg)?C.green:C.orange}}>{msg}</span>}
      </div>
    </div>
  </div>);
}

// ═══════ LENDER SUBMISSION CHECKLIST ═══════
function LenderChecklist({loan,deal,bare}){
  const [checks,setChecks]=useState({});
  const lenderKey=deal&&deal.lender?(Object.keys(LENDER_CHECKLISTS).find(k=>deal.lender.includes(k))||"Generic / Other"):"Generic / Other";
  const items=LENDER_CHECKLISTS[lenderKey]||LENDER_CHECKLISTS["Generic / Other"];
  const isCondo=loan&&loan.condo;
  const hasMortgage=loan&&loan.mb>0;
  const visible=items.filter(function(item){
    if(item.id==="hoa_cert"||item.id==="fha_condo")return isCondo;
    if(item.id==="payoff")return hasMortgage;
    return true;
  });
  const checked=visible.filter(function(item){return checks[item.id];}).length;
  const required=visible.filter(function(item){return item.required;});
  const reqDone=required.filter(function(item){return checks[item.id];}).length;
  const allReqDone=reqDone===required.length;
  const toggle=function(id){setChecks(function(p){var n=Object.assign({},p);n[id]=!n[id];return n;});};
  const reset=function(){setChecks({});};
  return(<div style={bare?{paddingTop:12}:{background:C.card,borderRadius:14,border:"1px solid "+(allReqDone?C.green+"40":C.border),padding:20,marginBottom:16}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        {!bare&&<h3 style={{fontSize:14,fontWeight:700,color:allReqDone?C.green:C.accentLight,margin:0}}>Submission Checklist</h3>}
        <Badge label={lenderKey} color={C.accent} bg={C.accentGlow}/>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:12,fontWeight:700,color:allReqDone?C.green:C.orange}}>{reqDone}/{required.length} required</span>
        <Btn small onClick={reset} style={{color:C.textDim,fontSize:10}}>Reset</Btn>
      </div>
    </div>
    {!deal.lender&&<div style={{fontSize:11,color:C.textDim,marginBottom:10,padding:"6px 10px",background:C.surfaceAlt,borderRadius:6}}>Pitch a scenario in the calculator to auto-select lender checklist</div>}
    {allReqDone&&<div style={{padding:"8px 12px",background:C.greenBg,borderRadius:6,marginBottom:10,fontSize:11,color:C.green,fontWeight:600}}>{Ic.chk} All required items checked — ready to submit!</div>}
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      {visible.map(function(item){var done=checks[item.id];return(
        <div key={item.id} onClick={function(){toggle(item.id);}} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:6,cursor:"pointer",background:done?C.greenBg:"transparent",transition:"background 0.1s"}} onMouseEnter={function(e){if(!done)e.currentTarget.style.background=C.surfaceAlt;}} onMouseLeave={function(e){if(!done)e.currentTarget.style.background="transparent";}}>
          <div style={{width:18,height:18,borderRadius:4,border:"2px solid "+(done?C.green:item.required?C.accentLight:C.borderLight),background:done?C.green:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {done&&<span style={{color:"#fff",fontSize:10}}>{Ic.chk}</span>}
          </div>
          <span style={{fontSize:12,color:done?C.green:C.text,flex:1,textDecoration:done?"line-through":"none",opacity:done?.7:1}}>{item.label}</span>
          {item.required&&!done&&<span style={{fontSize:9,color:C.accent,fontWeight:700,background:C.accentGlow,padding:"1px 5px",borderRadius:3}}>REQ</span>}
        </div>
      );})}
    </div>
    <div style={{marginTop:8,fontSize:10,color:C.textDim,textAlign:"right"}}>{checked} of {visible.length} items checked</div>
  </div>);
}

// ═══════ LOAN DETAIL (Editable) ═══════
// mktgLeads / setMktgLeads / adoptLeads: only for the fall-out modal's
// do-not-contact, which has to land on the mail-list row this file came from.
function LoanDetail({loans,setLoans,sel,setSel,setNav,borrowerAccts,setBorrowerAccts,sendSMS,genToken,dmCampaigns,staff,mktgLeads,setMktgLeads,adoptLeads,registerFlush}){
  const loan=sel?loans.find(function(l){return l.id===sel.id;}):null;
  const me=STAFF[staff]?staff:"reece";
  // Max (ownFilesOnly): his files only — `loans` holds nothing else — with no
  // portal controls, no lead links, no reassigning.
  const access=staffAccess(me);
  const ownOnly=!!access.ownFilesOnly;
  // "Lead assignment & Max on files" (src/features.js, off by default): off,
  // the header has no owner chip and no reassign menu. The owner itself is
  // still stamped on the file as before.
  const ownershipUi=useOwnershipUi();
  const practice=usePractice(me);
  // Max's follow-up + tasks card, and the documents he adds himself.
  const [newTask,sNewTask]=useState("");
  const [docBusy,sDocBusy]=useState(false);
  const [docMsg,sDocMsg]=useState("");
  const docInputRef=useRef(null);
  const [ownerMenu,sOwnerMenu]=useState(false);
  const [newCode,sNewCode]=useState("");
  // Ephemeral — the plaintext code is shown to admin once at creation so they
  // can share it with the borrower. After dismissal it's gone; re-sharing
  // requires revoke+recreate. Never persisted.
  const [justCreatedCode,setJustCreatedCode]=useState("");
  const [newNote,sNewNote]=useState("");
  // Send-Access modal — create portal access + email the borrower their code,
  // a one-click magic link, and the document checklist (Application + Supporting).
  const [showAccessModal,sShowAccessModal]=useState(false);
  const [accessEmail,sAccessEmail]=useState("");
  const [accessNote,sAccessNote]=useState("");
  const [accessDocReq,sAccessDocReq]=useState(null);
  const [accessCustom,sAccessCustom]=useState([]);
  const [accessNewCustom,sAccessNewCustom]=useState("");
  const [accessSending,sAccessSending]=useState(false);
  const [accessMsg,sAccessMsg]=useState("");
  // Identity fields (name/phone/email/address/DOB) lock once the deal reaches
  // Scheduled Counseling. This is a per-visit override for fixing a typo —
  // it resets whenever the page is left, so the default is always locked.
  const [idUnlocked,sIdUnlocked]=useState(false);
  const noteRef=useRef(null); // item #18 — auto-focus ref
  // Notes live in a slide-out drawer; collapsible sections remember explicit
  // clicks (otherwise each section's default applies — see secDefaults).
  const [notesOpen,sNotesOpen]=useState(false);
  const [openSec,sOpenSec]=useState({});
  // Fall-out modal: {stage, reason, note, followUp, dnc} or null.
  const [fallout,sFallout]=useState(null);
  const [callsOpen,sCallsOpen]=useState(false);
  const openNotes=function(){
    sNotesOpen(true);
    setTimeout(function(){noteRef.current&&noteRef.current.focus();},50);
  };

  // ── Notes save without a Save click ──
  // The drawer's draft is committed as ONE activity-log note on Enter, on
  // blur, when the drawer closes, and when this page unmounts or switches to
  // another file (effect cleanup below). The ref is cleared before writing, so
  // a blur that follows an Enter can never add the same note twice.
  const [noteSaved,sNoteSaved]=useState(false);
  const noteDraftRef=useRef("");
  const setLoansRef=useRef(setLoans);setLoansRef.current=setLoans;
  const meRef=useRef(me);meRef.current=me;
  const commitNoteDraft=function(id){
    var text=String(noteDraftRef.current||"").trim();
    noteDraftRef.current="";
    return writeNoteEntry(id,text);
  };
  // One activity-log note on file `id` (claims an unowned file).
  const writeNoteEntry=function(id,text){
    text=String(text||"").trim();
    if(!text||!id)return false;
    var ts=new Date();
    var entry={text:text,type:"note",timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),epoch:ts.getTime()};
    setLoansRef.current(function(prev){return prev.map(function(l){
      if(l.id!==id)return l;
      var n=Object.assign({},l,{activityLog:(l.activityLog||[]).concat([entry])});
      // Whoever works it owns it — a note claims an unowned file.
      if(!n.owner)n.owner=meRef.current;
      return n;
    });});
    return true;
  };
  const commitRef=useRef(commitNoteDraft);commitRef.current=commitNoteDraft;
  // The fall-out dialog's note is a note too: backing out of the dialog
  // (Cancel, the backdrop, Escape, leaving the file, signing out) keeps it on
  // the file instead of throwing it away. The ref is emptied as it is written
  // so it can never land twice.
  const falloutNoteRef=useRef("");
  falloutNoteRef.current=fallout?String(fallout.note||""):"";
  const commitFalloutNote=function(id){
    var text=String(falloutNoteRef.current||"").trim();
    falloutNoteRef.current="";
    if(!text)return false;
    return writeNoteEntry(id,text);
  };
  const falloutCommitRef=useRef(commitFalloutNote);falloutCommitRef.current=commitFalloutNote;
  const curLoanId=loan?loan.id:null;
  useEffect(function(){
    return function(){
      // Leaving this file (navigate away, or another file selected): the
      // half-typed note goes onto the file it was typed for.
      commitRef.current(curLoanId);
      falloutCommitRef.current(curLoanId);
      sNewNote("");sNoteSaved(false);sOwnerMenu(false);sFallout(null);sNewTask("");sDocMsg("");
    };
  },[curLoanId]);
  // Escape closes the fall-out dialog the same way Cancel does (note kept).
  const leaveFalloutRef=useRef(null);
  const falloutOpen=!!fallout;
  useEffect(function(){
    if(!falloutOpen)return;
    const h=function(e){if(e.key==="Escape"&&leaveFalloutRef.current)leaveFalloutRef.current();};
    document.addEventListener("keydown",h);
    return function(){document.removeEventListener("keydown",h);};
  },[falloutOpen]);
  // Logout / the idle sign-out ask for the same flush BEFORE the session ends.
  const curLoanIdRef=useRef(curLoanId);curLoanIdRef.current=curLoanId;
  useEffect(function(){
    if(!registerFlush)return;
    registerFlush("loanDetail",function(){
      var id=curLoanIdRef.current;if(!id)return;
      if(commitRef.current(id)){sNewNote("");sNoteSaved(true);}
      if(falloutCommitRef.current(id))sFallout(function(f){return f?Object.assign({},f,{note:""}):f;});
    });
    return function(){registerFlush("loanDetail",null);};
  },[registerFlush]);
  // item #18 — press N anywhere on page to open notes
  useEffect(function(){
    const handler=function(e){
      if(e.key==="n"&&!e.ctrlKey&&!e.metaKey&&document.activeElement.tagName!=="INPUT"&&document.activeElement.tagName!=="TEXTAREA"&&document.activeElement.tagName!=="SELECT"){
        e.preventDefault();
        openNotes();
      }
    };
    document.addEventListener("keydown",handler);
    return function(){document.removeEventListener("keydown",handler);};
  },[]);
  if(!loan) return(<div><Hdr t="Loan Detail" s="Select a loan from the pipeline or dashboard"/><Btn onClick={function(){setNav("pipeline");}}>Go to Pipeline</Btn></div>);

  const acct=borrowerAccts?borrowerAccts.find(function(a){return a.loanId===loan.id;}):null;
  const uploads=loan.uploads||[];
  const log=loan.activityLog||[];

  const addLogEntry=function(text,type){
    var ts=new Date();var entry={text:text,type:type||"note",timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})};
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loan.id)return l;
      return Object.assign({},l,{activityLog:(l.activityLog||[]).concat([entry])});
    });});
  };

  // why (stage only, optional): appended to the activity line — e.g. the
  // Post-Closing box that moved the file to Funded.
  const upd=function(field,value,why){
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loan.id)return l;
      var updated=Object.assign({},l);
      if(field==="stage"&&value!==l.stage){
        var ts=new Date();updated.activityLog=(l.activityLog||[]).concat([{text:"Stage changed: "+l.stage+" → "+value+(why?" — "+why:""),type:"stage",timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}]);
      }
      if(field.startsWith("deal.")){
        var dk=field.split(".")[1];
        updated.deal=Object.assign({},updated.deal||{});
        updated.deal[dk]=value;
      }else{
        updated[field]=value;
      }
      return updated;
    });});
  };

  // Several fields in one write, with one log line. claim=true: this is
  // working the file, so an unowned file becomes the signed-in person's.
  const patchLoan=function(patch,logText,logType,claim){
    var ts=new Date();
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loan.id)return l;
      var n=Object.assign({},l,patch);
      if(logText)n.activityLog=(l.activityLog||[]).concat([{text:logText,type:logType||"stage",timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),epoch:ts.getTime()}]);
      if(claim&&!n.owner)n.owner=me;
      return n;
    });});
  };

  // Every click-to-call gets a timestamp on the file (capped in wfPack). A
  // call claims an unowned file for whoever made it.
  const logCall=function(number){
    var now=new Date();
    var entry={at:now.toISOString(),date:now.toLocaleDateString(),time:now.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),number:number||""};
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loan.id)return l;
      var n=Object.assign({},l,{callClicks:(l.callClicks||[]).concat([entry]).slice(-CALL_CLICKS_MAX)});
      if(!n.owner)n.owner=me;
      return n;
    });});
  };

  // ── Owner ── a tracking tag ("whoever works it owns it"); reassigning is
  // only ever this explicit menu choice.
  const setOwner=function(key){
    sOwnerMenu(false);
    if(!STAFF[key]||loan.owner===key)return;
    var from=loan.owner&&STAFF[loan.owner]?STAFF[loan.owner].name:"unassigned";
    upd("owner",key);
    addLogEntry("Owner: "+from+" → "+STAFF[key].name+" (set by "+STAFF[me].name+")","system");
  };

  // ── Interest level + Booked (src/interest.js) ── the dot by the name, the
  // same control as the Pipeline rows. Stamped there (interestAt / bookedAt);
  // no log line — it is his read on the deal, changed as often as he likes.
  const setInterest=function(patch){setLoans(function(prev){return applyInterest(prev,loan.id,patch);});};
  const ist=interestStyle(loan);

  // The mail-list row this file came from: the file's own sourceLeadId link,
  // or a lead whose converted_loan_id points here (Marketing → Promote only
  // stamps that side). null when it isn't in memory / doesn't exist.
  const findLinkedLead=function(){
    var arr=mktgLeads||[];
    for(var i=0;i<arr.length;i++){var m=arr[i];if(m&&((loan.sourceLeadId&&m.id===loan.sourceLeadId)||m.converted_loan_id===loan.id))return m;}
    return null;
  };

  // ── Fall out ── same three doors + fields as the New Call console, so the
  // Fallout board reads the same whichever screen the file was dropped from.
  const askFallout=function(stage){
    sFallout(function(f){return{
      stage:stage,
      reason:(FALLOUT_REASONS[stage]||[])[0]||"",
      note:f?f.note:"",
      followUp:stage===FALLOUT_QUAL?isoDatePlus(90):"",
      dnc:false,
    };});
  };
  // (Do-not-contact lands on the mail-list lead — never from Max's login,
  // which has no leads.)
  const pickFalloutReason=function(v){
    sFallout(function(f){return Object.assign({},f,{reason:v},access.leads&&DNC_REASON_RE.test(v)?{dnc:true}:{});});
  };
  // Backing out of the dialog without moving the file: whatever was typed in
  // its note is kept as an ordinary note on the file.
  const leaveFallout=function(){
    if(loan)commitFalloutNote(loan.id);
    sFallout(null);
  };
  leaveFalloutRef.current=leaveFallout;
  const confirmFallout=function(){
    if(!fallout)return;
    var f=fallout;
    falloutNoteRef.current="";   // it goes on the file below — never twice
    var ts=new Date();
    var lead=findLinkedLead();
    var srcId=lead?lead.id:(loan.sourceLeadId||null);
    patchLoan({
      stage:f.stage,archived:true,queue:null,
      falloutReason:f.reason||"",falloutAt:ts.toISOString(),
      followUpDate:f.followUp||"",followUpNote:f.note||"",
    },"Stage changed: "+loan.stage+" → "+f.stage+" — "+(f.reason||"no reason given")+(f.note?" · "+f.note:"")
      +(f.dnc?(srcId&&setMktgLeads?" · marked do not contact ("+srcId+")":" · do not contact requested — no mailing-list record updated from here, mark DNC in Marketing"):""),"stage",true);
    // Mirror the disposition onto the mail-list row, as the New Call console
    // does: the fallout tag keeps the segment targetable from Marketing, and
    // a file has no do-not-contact flag of its own — the mailing list is what
    // has to stop. (Never from a login without the leads — Max.)
    if(access.leads&&setMktgLeads&&srcId){
      var tag=FALLOUT_TAGS[f.stage];
      var apply=function(m){
        var tags=(m.tags||[]).filter(function(t){return t!=="fallout-qual"&&t!=="fallout-denied"&&t!=="fallout-noapp";}).concat(tag?[tag]:[]);
        var n=Object.assign({},m,{tags:tags,updated_at:ts.toISOString()});
        if(f.dnc)Object.assign(n,{doNotContact:true,do_not_contact:true,status:"DNC"});
        return stampLeadOwner(n,me);
      };
      if(lead){
        setMktgLeads(function(prev){return prev.map(function(m){return m.id===srcId?apply(m):m;});});
      }else if(f.dnc&&isSupabaseReady()){
        // Outside the in-memory working set — fetch the row, adopt it, mark it.
        fetchLeadById(srcId).then(function(row){
          if(!row){window.alert("Couldn't find mailing-list record "+srcId+" to mark do not contact. Mark it DNC in Marketing.");return;}
          if(adoptLeads)adoptLeads([row]);
          setMktgLeads(function(prev){
            var has=prev.some(function(m){return m.id===srcId;});
            return has?prev.map(function(m){return m.id===srcId?apply(m):m;}):prev.concat([apply(row)]);
          });
        }).catch(function(e){window.alert("Couldn't reach the mailing list to mark "+srcId+" do not contact ("+((e&&e.message)||"unknown")+"). Mark it DNC in Marketing.");});
      }
    }
    sFallout(null);
  };

  // Enter / Add Note / blur / drawer close — all the same idempotent commit.
  const addNote=function(){
    if(commitNoteDraft(loan.id)){sNewNote("");sNoteSaved(true);}
  };
  const closeNotes=function(){addNote();sNotesOpen(false);};
  // Saving is automatic (debounced Supabase/localStorage sync in App), so
  // there is no manual save step or unsaved-changes state on this page.
  const deal=loan.deal||{};
  const $=function(n){return "$"+Math.round(parseInt(n)||0).toLocaleString();};

  // Hash the code before storing. The plaintext is shown to admin exactly
  // once via `justCreatedCode` — after dismissal it's gone and there's no
  // way to recover it (correct behavior; matches how password resets work).
  const createPortalAccess=async function(){
    var code=newCode.trim().toLowerCase()||genToken();
    var hashed=await hashPassword(code);
    var newAcct={id:"BA-"+Date.now(),loanId:loan.id,borrowerName:loan.b,passwordHash:hashed,created:new Date().toLocaleDateString()};
    setBorrowerAccts(function(p){return p.concat([newAcct]);});
    addLogEntry("Borrower portal access created (hashed)","system");
    setJustCreatedCode(code);
    sNewCode("");
  };
  const removePortalAccess=function(){
    if(!window.confirm("Revoke portal access for "+loan.b+"? They will no longer be able to log in."))return;
    setBorrowerAccts(function(p){return p.filter(function(a){return a.loanId!==loan.id;});});
    // The autosave only upserts — it never deletes — and the portal login API
    // checks every row in the table. Without this the "revoked" code kept
    // working and the account reappeared on the next reload.
    if(isSupabaseReady()){
      supabase.from('borrower_accounts').delete().eq('loan_id',loan.id).then(function(r){
        if(r&&r.error){console.warn("[portal] revoke delete failed",r.error.message||r.error);window.alert("Removed here, but the cloud delete failed ("+(r.error.message||"unknown")+"). The old code may still work — try again.");}
      });
    }
    addLogEntry("Borrower portal access revoked","system");
  };

  // ── Send-Access modal ──
  const openAccessModal=function(){
    sAccessEmail(loan.em||"");
    sAccessNote("");
    sAccessDocReq(defaultDocRequest(loan));
    sAccessCustom([]);
    sAccessNewCustom("");
    sAccessMsg("");
    sShowAccessModal(true);
  };
  // Toggle a supporting item / the application item, or edit the Quantum link.
  const toggleAccessItem=function(id){
    sAccessDocReq(function(dr){
      if(!dr)return dr;
      if(id==="app_docs")return Object.assign({},dr,{application:Object.assign({},dr.application,{on:!dr.application.on})});
      return Object.assign({},dr,{supporting:dr.supporting.map(function(s){return s.id===id?Object.assign({},s,{on:!s.on}):s;})});
    });
  };
  const setQuantumLink=function(v){
    sAccessDocReq(function(dr){return dr?Object.assign({},dr,{application:Object.assign({},dr.application,{link:v})}):dr;});
  };
  const addAccessCustom=function(){
    var v=accessNewCustom.trim();
    if(!v)return;
    sAccessCustom(function(p){return p.concat([v]);});
    sAccessNewCustom("");
  };
  const removeAccessCustom=function(i){
    sAccessCustom(function(p){return p.filter(function(_,idx){return idx!==i;});});
  };
  // Create (or rotate) the access code, persist it, and optionally email the
  // borrower. Because codes are stored hashed, "resend" issues a fresh code.
  const submitAccess=async function(sendEmail){
    if(accessSending)return;
    var email=accessEmail.trim();
    if(sendEmail&&!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)){sAccessMsg("Enter a valid email address to send the portal invite.");return;}
    sAccessSending(true);sAccessMsg("");
    try{
      var code=genToken();
      var hashed=await hashPassword(code);
      var newAcct={id:"BA-"+Date.now(),loanId:loan.id,borrowerName:loan.b,passwordHash:hashed,created:new Date().toLocaleDateString()};
      var sections=buildDocSections(accessDocReq,accessCustom);
      // Replace any existing account for this loan (rotate on resend).
      setBorrowerAccts(function(p){return p.filter(function(a){return a.loanId!==loan.id;}).concat([newAcct]);});
      // Persist the requested-docs list on the loan (inside the docs JSON —
      // no schema change). The borrower portal reads this for its checklist.
      setLoans(function(prev){return prev.map(function(l){
        if(l.id!==loan.id)return l;
        return Object.assign({},l,{docs:Object.assign({},l.docs||{},{requestedDocs:{sections:sections,note:accessNote.trim(),updatedAt:new Date().toISOString()}})});
      });});
      // Force an immediate borrower_accounts upsert so the code works the
      // moment the email lands (don't wait for the debounced autosave).
      if(isSupabaseReady()){
        // supabase-js resolves with {error} — it doesn't throw — so check it.
        try{
          var upR=await supabase.from('borrower_accounts').upsert(baToDb(newAcct));
          if(upR&&upR.error)throw new Error(upR.error.message||String(upR.error));
          // Rotate = the OLD account row must go too, or its code keeps working
          // (the login API scans every row) and it reappears after reload.
          var delR=await supabase.from('borrower_accounts').delete().eq('loan_id',loan.id).neq('id',newAcct.id);
          if(delR&&delR.error)console.warn("[send-access] old account delete failed",delR.error.message||delR.error);
        }catch(e){
          console.warn("[send-access] account pre-upsert failed",e&&e.message);
          window.alert("The access code could not be saved to the cloud ("+((e&&e.message)||"unknown")+"). Not sending the email — fix the connection and try again.");
          return;
        }
      }
      setJustCreatedCode(code);
      if(sendEmail){
        var token=null;
        if(isSupabaseReady()){try{token=(await supabase.auth.getSession()).data.session.access_token;}catch(e){}}
        var res=await BorrowerAPI.sendAccess({accessToken:token,loanId:loan.id,email:email,code:code,borrowerName:loan.b,note:accessNote.trim(),sections:sections});
        if(res.ok){addLogEntry("Borrower portal access created + invite emailed to "+email,"system");sShowAccessModal(false);}
        else{addLogEntry("Borrower portal access created (email send failed: "+(res.error||"unknown")+")","system");sAccessMsg("Access created, but the email failed to send ("+(res.error||"unknown")+"). The code is shown on the card — you can share it manually.");}
      }else{
        addLogEntry("Borrower portal access created (hashed)","system");
        sShowAccessModal(false);
      }
    }finally{sAccessSending(false);}
  };
  // Open a borrower document — legacy in-memory base64, or a short-lived
  // signed URL for Storage-backed entries (admin token authorizes any path).
  const adminViewDoc=async function(doc){
    if(doc.data){var w=window.open();w.document.write(doc.data.startsWith("data:image")?"<img src='"+doc.data+"' style='max-width:100%'/>":"<embed src='"+doc.data+"' width='100%' height='100%' type='application/pdf'/>");return;}
    // Max: the server's document link is Reece-only, so his own file's
    // documents are signed straight from Storage (RLS lets him read objects
    // under his own file ids only).
    if(ownOnly&&doc.storagePath&&isSupabaseReady()){
      try{
        var sr=await supabase.storage.from('loan-documents').createSignedUrl(doc.storagePath,300);
        if(sr&&sr.data&&sr.data.signedUrl)window.open(sr.data.signedUrl);
        else window.alert("Couldn't open the document ("+((sr&&sr.error&&sr.error.message)||"unknown")+"). Try again in a moment.");
      }catch(e){window.alert("Couldn't open the document ("+((e&&e.message)||"unknown")+"). Try again in a moment.");}
      return;
    }
    if(doc.storagePath&&isSupabaseReady()){
      var token=null;
      try{token=(await supabase.auth.getSession()).data.session.access_token;}catch(e){}
      var r=await BorrowerAPI.adminGetDocUrl({accessToken:token,path:doc.storagePath});
      if(r.ok&&r.url)window.open(r.url);
      else window.alert("Couldn't open the document ("+(r.error||"unknown")+"). Try again in a moment.");
    }
  };
  const removeUpload=function(idx){
    if(!window.confirm("Remove this document? This cannot be undone."))return;
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loan.id)return l;
      var u=(l.uploads||[]).slice();u.splice(idx,1);
      return Object.assign({},l,{uploads:u});
    });});
  };
  const updateUploadStatus=function(idx,status){
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loan.id)return l;
      var u=(l.uploads||[]).map(function(x,i){return i===idx?Object.assign({},x,{status:status}):x;});
      return Object.assign({},l,{uploads:u});
    });});
  };

  // ── Max's tracking: follow-up date + tasks (ownOnly) ──
  // He has no New Call console to set a call back, so it is set here; a due
  // one shows in his Prospect lane (prospectLaneCallbacks). Tasks are the
  // file's own list (the pipeline badge + hover to-dos read it).
  const logLine=function(text,type,ts){return{text:text,type:type||"system",timestamp:ts.toLocaleString(),date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),epoch:ts.getTime()};};
  const setFollowUpIn=function(days){
    var d=days==null?"":isoDatePlus(days);
    var ts=new Date();
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loan.id)return l;
      return Object.assign({},l,{followUpDate:d,activityLog:(l.activityLog||[]).concat([logLine(d?"Call back set for "+fmtDob(d):"Call back cleared","system",ts)])});
    });});
  };
  const addTask=function(){
    var t=String(newTask||"").trim();
    if(!t)return;
    var ts=new Date();
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loan.id||(l.tasks||[]).indexOf(t)>=0)return l;
      return Object.assign({},l,{tasks:(l.tasks||[]).concat([t]),activityLog:(l.activityLog||[]).concat([logLine("Task added: "+t,"system",ts)])});
    });});
    sNewTask("");
  };
  const doneTask=function(t){
    var ts=new Date();
    setLoans(function(prev){return prev.map(function(l){
      if(l.id!==loan.id)return l;
      return Object.assign({},l,{tasks:(l.tasks||[]).filter(function(x){return x!==t;}),activityLog:(l.activityLog||[]).concat([logLine("Task done: "+t,"system",ts)])});
    });});
  };
  // A document Max adds to his own file: stored at <file id>/<time>-<name>
  // in the loan-documents bucket — the same layout as a borrower's portal
  // upload, which is what his storage policy allows (supabase/2026-10-02-
  // max-own-pipeline.sql). Practice mode uploads nothing.
  const uploadDoc=async function(file){
    if(!file||!loan)return;
    if(isPracticeOrUnknown(me)){sDocMsg("Practice mode — documents aren't uploaded. Turn it off in Settings to add one for real.");return;}
    if(!isSupabaseReady()){sDocMsg("Adding a document needs the cloud connection.");return;}
    var id=loan.id;
    var safe=(String(file.name||"document").replace(/[^A-Za-z0-9._-]+/g,"_").replace(/^_+/,"").slice(-120))||"document";
    var path=id+"/"+Date.now()+"-"+safe;
    sDocBusy(true);sDocMsg("");
    try{
      var r=await supabase.storage.from('loan-documents').upload(path,file,{upsert:false,contentType:file.type||undefined});
      if(r&&r.error)throw r.error;
      var ts=new Date();
      var ft=String(file.type||"");
      var entry={name:file.name||safe,type:/pdf/i.test(ft)?"PDF":/image/i.test(ft)?"Image":"Document",date:ts.toLocaleDateString(),time:ts.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),
        status:"Received",storagePath:path,size:file.size||0,epoch:ts.getTime(),note:"Added by "+STAFF[me].name};
      setLoans(function(prev){return prev.map(function(l){
        if(l.id!==id)return l;
        return Object.assign({},l,{uploads:(l.uploads||[]).concat([entry]),activityLog:(l.activityLog||[]).concat([logLine("Document added: "+entry.name,"doc",ts)])});
      });});
      sDocMsg("✓ Added "+entry.name);
    }catch(e){
      sDocMsg("Couldn't add it ("+((e&&e.message)||"unknown")+"). A file made in the last few seconds may not be saved yet — try again in a moment.");
    }finally{sDocBusy(false);}
  };

  // onPick (selects only): route the choice through a handler instead of a
  // plain upd — the Stage select uses it so fallout moves go through the modal.
  const er=function(label,field,val,money,select,opts,onPick){
    return(<div key={field} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:"1px solid "+C.border}}>
      <span style={{fontSize:11,color:C.textDim,fontWeight:600,minWidth:140,textTransform:"uppercase",letterSpacing:.3}}>{label}</span>
      <div style={{flex:1}}>
        {select?<Sel value={val||""} onChange={function(e){if(onPick)onPick(e.target.value);else upd(field,e.target.value);}} options={opts}/>
        :money?<MoneyInp value={String(val||"")} onChange={function(e){upd(field,parseInt(e.target.value)||0);}} placeholder="0"/>
        :<Inp value={val||""} onChange={function(e){upd(field,e.target.value);}} style={{padding:"7px 12px",fontSize:12}}/>}
      </div>
    </div>);
  };

  // Read-only display row — used for identity fields once the deal is locked.
  const lr=function(label,val){
    return(<div key={"lr-"+label} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 0",borderBottom:"1px solid "+C.border}}>
      <span style={{fontSize:11,color:C.textDim,fontWeight:600,minWidth:140,textTransform:"uppercase",letterSpacing:.3}}>{label}</span>
      <div style={{flex:1,display:"flex",alignItems:"center",gap:8,minWidth:0}}>
        <span style={{fontSize:13,color:C.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{val||"—"}</span>
        <span style={{fontSize:10,color:C.textDim,flexShrink:0}}>🔒</span>
      </div>
    </div>);
  };

  // Identity lock: at Scheduled Counseling or beyond these are facts on file,
  // not fields that look like they still need editing.
  const idLocked=stageAtOrPast(loan,LOCK_STAGE)&&!idUnlocked;
  const dobAge=loanAge(loan);
  const counselingStage=stageAtOrPast(loan,LOCK_STAGE);
  // The counselor package is only chased between Scheduled Counseling and
  // Docs Out — the same window topTodos flags it in. Past that (a file moved
  // on, or a deal entered by hand at Closed) an unticked toggle is just "not
  // recorded", never a red alarm pointing at a to-do that isn't there.
  const counselorDue=counselingStage&&!stageAtOrPast(loan,"Docs Out")&&!loan.counselorSent;
  const borrowerCounseling=loan.counseling||null;

  // ── Deal snapshot numbers ──
  // Everything here is an OUTPUT of pricing (derived values are never
  // editable) — the calculator's pitch writes deal/comm, this page just reads.
  const pitched=!!(deal.product||(deal.grossPL>0));
  const totalCosts=deal.totalCosts>0?deal.totalCosts:((deal.origination||0)+(deal.closingCosts||0)+(deal.mipUpfront||0));
  // HomeSafe Second: the file (the card's HS2 pick or an HS2 pitched on it)
  // and the live pitch — whose amount is FOA's or the calculator's estimate.
  const hs2File=isHs2File(loan);
  const hs2Pitch=!deal.manual&&deal.dealType==="hs2";
  // Pitched in the calculator's HomeSafe Second mode (PL in, pay out): no
  // costs / cash were figured from anything he typed — the snapshot shows
  // FOA's amount and the pay instead (deal.payOnly, Calc pitchScenario).
  const payOnly=hs2Pitch&&!!deal.payOnly;
  const stageCol=STC[loan.stage]||C.accent;
  const isOut=FALLOUT_STAGES.indexOf(loan.stage)>=0;
  const showAppraisal=stageAtOrPast(loan,"Appraisal Ordered")||!!(loan.appraisalOrdered||loan.appraisalScheduled||loan.appraisalDone);
  const notes=log.filter(function(e){return e.type==="note";});
  const lastNote=notes.length?notes[notes.length-1]:null;
  const calls=loan.callClicks||[];
  const lastCall=calls.length?calls[calls.length-1]:null;
  const pendingDocs=uploads.filter(function(u){return (u.status||"Received")!=="Complete";}).length;
  // Section open state: an explicit click wins, otherwise the default below.
  const secDefaults={details:false,checklist:false,portal:false,docs:pendingDocs>0,history:false,reimb:owedItems(loan).length>0};
  const isOpen=function(k){return openSec[k]!==undefined?openSec[k]:!!secDefaults[k];};
  const toggleSec=function(k){sOpenSec(function(p){var n=Object.assign({},p);n[k]=!isOpen(k);return n;});};
  const goCalc=function(){setSel(loan);setNav("inquiry");};
  // Off the Fallout board and back onto a live lane: archived must go with
  // the fallout stage, or the file lands in no lane and on no board at all.
  const reviveTo=function(back){
    if(!window.confirm("Move "+loan.b+" back onto the pipeline at \""+back+"\"?"))return;
    patchLoan({stage:back,archived:false,falloutReason:null,falloutAt:null},"Revived from "+loan.stage+" → "+back,"stage",true);
  };
  const reviveToPipeline=function(){reviveTo(pitched?"Pitched":"Prospect");};
  // Details → Stage. A plain write is only right between two LIVE stages:
  //   live → fallout, fallout → other fallout: the fall-out modal (reason,
  //     archived:true, queue cleared, follow-up) — stage changes on confirm.
  //   fallout → live: revive (archived:false, reason/at cleared).
  // The header's stage select and the Post-Closing Tracker's auto-Funded use
  // this same path (why = what moved it, for the activity line).
  const changeStage=function(v,why){
    if(!v||v===loan.stage)return;
    if(FALLOUT_STAGES.indexOf(v)>=0){askFallout(v);return;}
    if(isOut){reviveTo(v);return;}
    upd("stage",v,why);
  };
  // ── Reimbursements owed to you ── the header chip opens the section and
  // scrolls to it.
  const owedNow=owedTotal(loan);
  const owedCount=owedItems(loan).length;
  const goReimb=function(){
    sOpenSec(function(p){return Object.assign({},p,{reimb:true});});
    setTimeout(function(){var el=document.getElementById("ld-sec-reimb");if(el&&typeof el.scrollIntoView==="function"){try{el.scrollIntoView({behavior:"smooth",block:"start"});}catch(e){/* older browsers */}}},30);
  };
  const stageOpts=STAGES.indexOf(loan.stage)<0&&FALLOUT_STAGES.indexOf(loan.stage)<0&&loan.stage?[loan.stage]:[];

  const tile=function(label,val,accent){
    var has=val>0;
    return(<div style={{flex:"1 1 150px",minWidth:140,padding:"16px 18px",borderRadius:12,background:C.surfaceAlt,border:"1px solid "+C.border}}>
      <div style={{fontSize:10,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>{label}</div>
      <div style={{fontSize:24,fontWeight:800,color:has?(accent||C.text):C.textDim,letterSpacing:"-0.02em"}}>{has?$(val):"—"}</div>
    </div>);
  };
  const contactItem=function(icon,content){
    return(<div style={{display:"flex",alignItems:"center",gap:8,fontSize:13.5,color:C.text,minWidth:0}}>
      <span style={{fontSize:13,opacity:.7,flexShrink:0}}>{icon}</span>
      <div style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{content}</div>
    </div>);
  };
  const card={background:C.card,borderRadius:14,border:"1px solid "+C.border,padding:22,marginBottom:14};

  return(<div style={{maxWidth:1040,margin:"0 auto"}}>
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
      <Btn small onClick={function(){setNav("pipeline");}} style={{color:C.textDim}}>{"←"} Pipeline</Btn>
      {access.dashboard&&<Btn small onClick={function(){setNav("dashboard");}} style={{color:C.textDim}}>Command Center</Btn>}
    </div>

    {/* ── Header: who, how to reach them, what to do next ── */}
    <div style={Object.assign({},card,{padding:"22px 24px"})}>
      <div style={{display:"flex",alignItems:"flex-start",gap:16,flexWrap:"wrap"}}>
        <div style={{flex:"1 1 320px",minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:4}}>
            <InterestMenu loan={loan} onSet={setInterest}/>
            <h1 data-interest-name={loan.id} title={ist.label||undefined} style={Object.assign({fontSize:26,fontWeight:800,color:C.text,margin:0,letterSpacing:"-0.02em"},interestNameStyle(loan),ist.booked?{background:C.booked,padding:"0 8px",borderRadius:6,boxShadow:"inset 0 -2px 0 "+C.bookedEdge}:null)}>{loan.b||"(unnamed)"}</h1>
            {/* Stage — changed right here at the top (Reece, 10/10/26). The same
                list and the same changeStage as Details → Stage below, so a
                fallout pick opens the fall-out dialog and a fallout file
                revives; the pill keeps the stage's colour. */}
            <span style={{position:"relative",display:"inline-flex",alignItems:"center"}}>
              <select data-stage-select={loan.id} aria-label="Stage" title="Change the stage" value={loan.stage||""} onChange={function(e){changeStage(e.target.value);}}
                style={{appearance:"none",WebkitAppearance:"none",MozAppearance:"none",padding:"4px 26px 4px 11px",borderRadius:20,fontSize:11.5,fontWeight:700,letterSpacing:.3,color:stageCol,background:stageCol+"18",border:"1px solid "+stageCol+"55",cursor:"pointer",outline:"none",fontFamily:"'Mulish',system-ui,sans-serif",minHeight:26,maxWidth:240}}>
                {stageOpts.map(function(s){return <option key={s} value={s} style={{color:C.text,background:C.card}}>{s}</option>;})}
                <optgroup label="Pipeline">{STAGES.map(function(s){return <option key={s} value={s} style={{color:C.text,background:C.card}}>{s}</option>;})}</optgroup>
                <optgroup label="Fallout">{FALLOUT_STAGES.map(function(s){return <option key={s} value={s} style={{color:C.text,background:C.card}}>{s}</option>;})}</optgroup>
              </select>
              <span aria-hidden="true" style={{position:"absolute",right:10,pointerEvents:"none",fontSize:9,color:stageCol}}>▼</span>
            </span>
            {ist.label&&<span data-interest-label="1" style={{fontSize:11,fontWeight:800,color:ist.color||C.text,background:ist.booked?C.booked:C.surfaceAlt,border:"1px solid "+(ist.booked?C.bookedEdge:C.border),padding:"2px 9px",borderRadius:20,whiteSpace:"nowrap"}}>{ist.label}</span>}
            {owedNow>0&&<button data-owed-chip={loan.id} onClick={goReimb} title={owedCount+" reimbursement"+(owedCount===1?"":"s")+" owed to you on this file — show them"}
              style={{fontSize:11.5,fontWeight:800,color:C.clayDeep,background:C.white,border:"1px solid "+C.clay+"99",padding:"3px 10px",borderRadius:20,whiteSpace:"nowrap",cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif"}}>Owed to you: {fmtMoney(owedNow)}</button>}
            {/* Owner — who works this file. Click to reassign (the only way an
                existing owner ever changes). Max's files are his: no menu.
                Only while the owner UI is on (src/features.js). */}
            {ownOnly?<OwnerChip owner={ownerOf(loan)} size={20} label={STAFF[ownerOf(loan)]?STAFF[ownerOf(loan)].name:ownerOf(loan)} title="Your file"/>
            :!ownershipUi?null
            :<span data-owner-control={loan.id} style={{position:"relative",display:"inline-flex"}}>
              <OwnerChip owner={ownerOf(loan)} size={20} label={STAFF[ownerOf(loan)]?STAFF[ownerOf(loan)].name+(loan.owner?"":" (unassigned)")+" ▾":ownerOf(loan)}
                title={loan.owner?"Worked by "+(STAFF[loan.owner]?STAFF[loan.owner].name:loan.owner)+" — click to reassign":"No owner recorded yet — counts as Reece's. Click to assign."}
                onClick={function(){sOwnerMenu(!ownerMenu);}}/>
              {ownerMenu&&<>
                <div onClick={function(){sOwnerMenu(false);}} style={{position:"fixed",inset:0,zIndex:900}}/>
                <div style={{position:"absolute",top:"calc(100% + 6px)",left:0,zIndex:901,background:C.card,border:"1px solid "+C.border,borderRadius:10,boxShadow:"0 10px 30px rgba(6,26,48,0.22)",padding:6,minWidth:190}}>
                  <div style={{fontSize:10,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6,padding:"4px 8px 6px"}}>Who works this file</div>
                  {Object.keys(STAFF).map(function(k){
                    var on=loan.owner===k;
                    return <button key={k} onClick={function(){setOwner(k);}} style={{display:"flex",alignItems:"center",gap:9,width:"100%",padding:"8px 10px",borderRadius:7,border:"none",background:on?C.accentGlow:"transparent",color:C.text,fontSize:13,fontWeight:700,cursor:"pointer",textAlign:"left",fontFamily:"'Mulish',system-ui,sans-serif"}}>
                      <OwnerChip owner={k}/>{STAFF[k].name}{k===me?<span style={{fontSize:11,color:C.textDim,fontWeight:500}}>(you)</span>:null}
                      {on&&<span style={{marginLeft:"auto",color:C.green}}>✓</span>}
                    </button>;
                  })}
                </div>
              </>}
            </span>}
          </div>
          <div style={{fontSize:11.5,color:C.textDim,marginBottom:14}}>{loan.id}{dobAge?" · age "+dobAge:""}{loan.src?" · "+loan.src:""}</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {loan.ph?<div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <CallBtn phone={loan.ph} onCall={logCall}/>
              {calls.length>0&&<button onClick={function(){sCallsOpen(!callsOpen);}} style={{background:"none",border:"none",padding:0,cursor:"pointer",fontSize:11.5,color:C.textDim,fontFamily:"'Mulish',system-ui,sans-serif"}}>
                Called {calls.length}× · last {lastCall.date} {lastCall.time} <span style={{display:"inline-block",transform:callsOpen?"rotate(90deg)":"none",transition:"transform .15s"}}>›</span>
              </button>}
            </div>:contactItem("📞",<span style={{color:C.textDim}}>No phone on file</span>)}
            {callsOpen&&calls.length>0&&<div style={{maxHeight:150,overflowY:"auto",padding:"6px 12px",borderRadius:8,background:C.surfaceAlt,border:"1px solid "+C.border,maxWidth:340}}>
              {calls.slice().reverse().map(function(c,i){return(
                <div key={c.at+i} style={{display:"flex",justifyContent:"space-between",gap:12,padding:"4px 0",fontSize:11.5,color:C.textMuted,borderBottom:i<calls.length-1?"1px solid "+C.border:"none"}}>
                  <span>{c.date}</span><span style={{fontWeight:600,color:C.text}}>{c.time}</span>
                </div>
              );})}
            </div>}
            {contactItem("✉",loan.em?<a href={"mailto:"+loan.em} style={{color:C.accent,textDecoration:"none",fontWeight:600}}>{loan.em}</a>:<span style={{color:C.textDim}}>No email on file</span>)}
            {contactItem("📍",loan.prop?<span style={{fontWeight:600}}>{loan.prop}</span>:<span style={{color:C.textDim}}>No property address</span>)}
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",justifyContent:"flex-end"}}>
          <Btn primary small onClick={goCalc}>Calculate</Btn>
          <Btn small onClick={openNotes}>Notes{notes.length?" · "+notes.length:""}</Btn>
          {isOut
            ?<Btn small onClick={reviveToPipeline} style={{color:C.accent}}>Back to pipeline</Btn>
            :<Btn small onClick={function(){askFallout(pitched?FALLOUT_QUAL:FALLOUT_NOAPP);}} style={{color:C.red}}>Fall out</Btn>}
        </div>
      </div>
      {isOut&&<div style={{marginTop:14,padding:"10px 14px",borderRadius:8,background:stageCol+"12",border:"1px solid "+stageCol+"40",fontSize:12,color:C.text}}>
        <strong style={{color:stageCol}}>{loan.stage}</strong>{" — "}{loan.falloutReason||"no reason recorded"}{loan.followUpDate?" · call back "+String(loan.followUpDate).slice(0,10):""}
      </div>}
    </div>

    {/* ── Deal snapshot ── */}
    <div style={card}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <h3 style={{fontSize:14,fontWeight:800,color:C.text,margin:0}}>Deal Snapshot</h3>
        {pitched
          ?<span style={{fontSize:12,color:C.textMuted}}>{[deal.product,deal.lender,deal.rate].filter(Boolean).join(" · ")}</span>
          :<Badge label="Not priced yet" color={C.textDim} bg={C.textDim+"15"}/>}
        {/* Figures Reece typed from the lender rather than the calculator's
            are never shown as if they were derived. */}
        {deal.manual&&<span title="Typed from the lender's paperwork — not priced by the calculator"><Badge label={MANUAL_DEAL_LABEL} color={C.purple} bg={C.purpleBg}/></span>}
        {!deal.manual&&!payOnly&&Number(deal.lenderAmount)>0&&<span title="The loan amount is the lender's figure, typed over the calculator's"><Badge label="Lender's amount" color={C.purple} bg={C.purpleBg}/></span>}
        {hs2File&&<span title="FOA's HomeSafe Second — a 2nd lien behind a first mortgage that stays in place"><Badge label={HS2_LOAN_TYPE} color={C.navy} bg={C.navy+"14"}/></span>}
      </div>
      {deal.manual&&<div style={{fontSize:11.5,color:C.textMuted,marginTop:-6,marginBottom:12}}>
        {MANUAL_DEAL_LABEL}{deal.enteredAt?" on "+new Date(deal.enteredAt).toLocaleDateString():""} — the lender's figures as typed, not the calculator's.{deal.closeDate?" Close date "+deal.closeDate+".":""}
      </div>}
      {/* Pitched on the lender's own figure (the calculator card's "Lender's
          amount"): say what was typed and what the calculator had said. */}
      {!deal.manual&&!payOnly&&Number(deal.lenderAmount)>0&&<div style={{fontSize:11.5,color:C.textMuted,marginTop:-6,marginBottom:12,lineHeight:1.5}}>
        Lender figure — <strong style={{color:C.text}}>{$(deal.lenderAmount)}</strong>
        {Number(deal.calcAmount)>0?" (calculator said "+$(deal.calcAmount)+")":""}
        {deal.cltv!=null&&!isNaN(Number(deal.cltv))?" · CLTV "+cltvText(deal.cltv):""}
        {" — typed from the lender's software; cash and comp are figured from it."}
        {deal.lenderAmountRecheck&&<div style={{color:C.orange,fontWeight:700}}>Re-check — entered before the DOB / value / balance changed</div>}
      </div>}
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        {tile("Home value",loan.hv)}
        {tile(hs2File?"First lien (stays)":deal.firstLien!=null?"First lien":"Balance",loan.mb)}
        {tile(deal.manual?"Loan amount · by hand":hs2Pitch?(Number(deal.lenderAmount)>0?"HomeSafe Second · FOA's":"HomeSafe Second · estimate"):Number(deal.lenderAmount)>0?"Principal limit · lender's":"Principal limit",deal.grossPL,C.accent)}
        {payOnly
          ?<div data-snapshot-pay="1" style={{flex:"1 1 150px",minWidth:140,padding:"16px 18px",borderRadius:12,background:C.surfaceAlt,border:"1px solid "+C.border}}>
            <div style={{fontSize:10,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Your pay</div>
            <div style={{fontSize:24,fontWeight:800,color:C.gold,letterSpacing:"-0.02em"}}>{payCents(deal.pay!=null?deal.pay:loan.comm)}</div>
          </div>
          :tile("Closing costs",totalCosts)}
      </div>
      {pitched
        ?<div style={{display:"flex",alignItems:"center",gap:18,flexWrap:"wrap",marginTop:14,fontSize:12.5,color:C.textMuted}}>
          {payOnly&&<span data-snapshot-paydetail="1">{deal.payDetail?"Pay: "+deal.payDetail+" — ":""}figured from FOA's amount; costs and cash are in FOA's software</span>}
          {!payOnly&&deal.cashToBorrower>0&&<span>Available to borrower <strong style={{color:C.green}}>{$(deal.cashToBorrower)}</strong></span>}
          {!payOnly&&deal.year1Cash!=null&&Number(deal.year1Cash)<Number(deal.cashToBorrower||0)&&<span>Available year 1 <strong style={{color:C.text}}>{$(deal.year1Cash)}</strong></span>}
          {!payOnly&&loan.comm>0&&<span>Your comp <strong style={{color:C.text}}>{$(loan.comm)}</strong></span>}
          <span style={{flex:1}}/>
          <Btn small onClick={goCalc}>Re-run numbers</Btn>
        </div>
        :<div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",marginTop:14,padding:"12px 14px",borderRadius:10,background:C.accentGlow}}>
          <span style={{fontSize:12.5,color:C.accentLight,flex:1,minWidth:200}}>Run the calculator to fill in principal limit, costs and comp.</span>
          <Btn primary small onClick={goCalc}>Calculate</Btn>
        </div>}
      {(lastNote||loan.followUpDate)&&<div style={{marginTop:14,paddingTop:14,borderTop:"1px solid "+C.border,display:"flex",flexDirection:"column",gap:6}}>
        {loan.followUpDate&&!isOut&&<div style={{fontSize:12,color:C.orange,fontWeight:600}}>Follow up {String(loan.followUpDate).slice(0,10)}{loan.followUpNote?" — "+loan.followUpNote:""}</div>}
        {lastNote&&<div onClick={openNotes} style={{fontSize:12.5,color:C.textMuted,cursor:"pointer",lineHeight:1.5}}>
          <span style={{fontSize:10,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6,marginRight:8}}>Last note · {lastNote.date}</span>{lastNote.text}
        </div>}
      </div>}
    </div>

    {/* ── Follow-up + tasks (Max's own files) — he has no New Call console
        or Command Center, so a file's call back and to-dos are kept here. ── */}
    {ownOnly&&(function(){
      var fu=loan.followUpDate?String(loan.followUpDate).slice(0,10):"";
      var due=!!fu&&isDueBy(fu);
      var tasks=loan.tasks||[];
      var lbl={display:"block",fontSize:10,fontWeight:700,color:C.textDim,marginBottom:5,textTransform:"uppercase",letterSpacing:.5};
      return(<div data-tracking="1" style={Object.assign({},card,due?{border:"1px solid "+C.orange+"60"}:{})}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
          <h3 style={{fontSize:14,fontWeight:800,color:C.text,margin:0}}>Follow-up &amp; tasks</h3>
          {fu?<Badge label={(due?"Call back due ":"Call back ")+fmtDob(fu)} color={due?C.orange:C.accent} bg={(due?C.orange:C.accent)+"18"}/>:<Badge label="No call back set" color={C.textDim} bg={C.textDim+"15"}/>}
          {tasks.length>0&&<Badge label={tasks.length+" task"+(tasks.length===1?"":"s")} color={C.orange} bg={C.orangeBg}/>}
        </div>
        <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div style={{flex:"0 1 190px",minWidth:160}}>
            <label style={lbl}>Call back on</label>
            <Inp type="date" value={fu} onChange={function(e){upd("followUpDate",e.target.value||"");}} style={{padding:"7px 12px",fontSize:12,colorScheme:"light"}}/>
          </div>
          <div style={{flex:"1 1 260px",minWidth:200}}>
            <label style={lbl}>What for</label>
            <Inp value={loan.followUpNote||""} onChange={function(e){upd("followUpNote",e.target.value);}} placeholder="e.g. spouse back from travel — go over the numbers" style={{padding:"7px 12px",fontSize:12}}/>
          </div>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginTop:8}}>
          {[{d:1,l:"Tomorrow"},{d:7,l:"1 week"},{d:30,l:"30 days"},{d:90,l:"90 days"}].map(function(o){
            var on=fu===isoDatePlus(o.d);
            return <button key={o.d} onClick={function(){setFollowUpIn(o.d);}} style={{padding:"4px 11px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+(on?C.orange:C.border),background:on?C.orangeBg:"transparent",color:on?C.orange:C.textMuted}}>{o.l}</button>;
          })}
          {fu&&<button onClick={function(){setFollowUpIn(null);}} style={{padding:"4px 11px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+C.border,background:"transparent",color:C.textDim}}>Clear</button>}
          <span style={{fontSize:10.5,color:C.textDim,marginLeft:4}}>Shows in your Prospect lane once it's due.</span>
        </div>
        <div style={{marginTop:16,paddingTop:14,borderTop:"1px solid "+C.border}}>
          <label style={lbl}>Tasks</label>
          {tasks.length===0?<div style={{fontSize:12,color:C.textDim,padding:"4px 0 8px"}}>Nothing on the list.</div>
          :<div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
            {tasks.map(function(t,i){return(<div key={t+"|"+i} data-task-row="1" style={{display:"flex",alignItems:"center",gap:10,padding:"7px 12px",borderRadius:8,background:C.surfaceAlt,border:"1px solid "+C.border}}>
              <span style={{flex:1,minWidth:0,fontSize:12.5,color:/^URGENT/.test(t)?C.red:C.text,fontWeight:/^URGENT/.test(t)?700:500,wordBreak:"break-word"}}>{t}</span>
              <Btn small onClick={function(){doneTask(t);}} style={{color:C.green,fontSize:11}}>✓ Done</Btn>
            </div>);})}
          </div>}
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <Inp value={newTask} onChange={function(e){sNewTask(e.target.value);}} onKeyDown={function(e){if(e.key==="Enter"){e.preventDefault();addTask();}}} placeholder="Add a task — e.g. get the payoff statement" style={{padding:"7px 12px",fontSize:12,flex:1}}/>
            <Btn small onClick={addTask} disabled={!String(newTask||"").trim()}>Add task</Btn>
          </div>
        </div>
      </div>);
    })()}

    {/* Counseling — appears once the deal reaches Scheduled Counseling */}
    {counselingStage&&<div data-counseling={loan.counselorSent?"sent":counselorDue?"due":"not-recorded"} style={Object.assign({},card,loan.counselorSent||counselorDue?{border:"1px solid "+(loan.counselorSent?C.green:C.orange)+"50"}:{})}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14,flexWrap:"wrap"}}>
        <h3 style={{fontSize:14,fontWeight:700,color:loan.counselorSent?C.green:counselorDue?C.orange:C.text,margin:0}}>Counseling</h3>
        {loan.counselorSent?<Badge label="Package sent ✓" color={C.green} bg={C.greenBg}/>:counselorDue?<Badge label="Package NOT sent" color={C.red} bg={C.redBg}/>:<Badge label="Not recorded" color={C.textDim} bg={C.textDim+"15"}/>}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:"1px solid "+C.border}}>
        <span style={{fontSize:11,color:C.textDim,fontWeight:600,minWidth:140,textTransform:"uppercase",letterSpacing:.3}}>Counseling Date</span>
        <div style={{flex:1}}><Inp type="date" value={loan.counselingDate||(borrowerCounseling&&borrowerCounseling.date)||""} onChange={function(e){upd("counselingDate",e.target.value);}} style={{padding:"7px 12px",fontSize:12,colorScheme:"light"}}/></div>
      </div>
      {borrowerCounseling&&<div style={{fontSize:11,color:C.textDim,padding:"8px 0",borderBottom:"1px solid "+C.border}}>Borrower scheduled via portal: {borrowerCounseling.date}{borrowerCounseling.time?" @ "+borrowerCounseling.time:""}</div>}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"12px 0 2px"}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:C.text}}>Borrower info sent to counselor</div>
          <div style={{fontSize:10,color:C.textDim,marginTop:2}}>{loan.counselorSent?"Confirmed "+(loan.counselorSentAt||""):counselorDue?"Until confirmed, this stays on the deal's Top To-Do list":"Not recorded — the file is past Docs Out, so it isn't on the to-do list"}</div>
        </div>
        <button onClick={function(){var next=!loan.counselorSent;upd("counselorSent",next);upd("counselorSentAt",next?new Date().toLocaleDateString():null);addLogEntry(next?"Confirmed: borrower info package sent to counselor":"Counselor package marked NOT sent","system");}} style={{width:44,height:24,borderRadius:12,border:"none",background:loan.counselorSent?C.green:C.borderLight,cursor:"pointer",position:"relative",transition:"background 0.2s"}}>
          <div style={{width:18,height:18,borderRadius:"50%",background:C.white,position:"absolute",top:3,left:loan.counselorSent?23:3,transition:"left 0.2s"}}/>
        </button>
      </div>
      {counselorDue&&<div style={{marginTop:10,padding:"8px 12px",background:C.redBg,borderRadius:6,fontSize:11,color:C.red,fontWeight:600}}>⚠ Send the borrower's info over to the counselor — flagged on the pipeline hover to-do list</div>}
    </div>}

    {/* Appraisal — only once the file has actually reached that point */}
    {showAppraisal&&<div style={card}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
        <h3 style={{fontSize:14,fontWeight:700,color:C.purple,margin:0}}>Appraisal</h3>
        {loan.appraisalDone?<Badge label="Done" color={C.green} bg={C.greenBg}/>:loan.appraisalScheduled?<Badge label="Scheduled" color={C.accent} bg={C.accentGlow}/>:loan.appraisalOrdered?<Badge label="Ordered" color={C.orange} bg={C.orangeBg}/>:deal.manual?<span title="Entered by hand — no appraisal dates on file"><Badge label="—" color={C.textDim} bg={C.textDim+"15"}/></span>:<Badge label="Not Started" color={C.textDim} bg={C.textDim+"15"}/>}
      </div>
      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
        {[{l:"Ordered",k:"appraisalOrdered"},{l:"Scheduled",k:"appraisalScheduled"},{l:"Completed",k:"appraisalDone"}].map(function(f){return(
          <div key={f.k} style={{flex:"1 1 160px"}}>
            <div style={{fontSize:10,color:loan[f.k]?C.green:C.textDim,fontWeight:700,textTransform:"uppercase",letterSpacing:.4,marginBottom:4}}>{loan[f.k]?"✓ ":""}{f.l}</div>
            <Inp type="date" value={loan[f.k]||""} onChange={function(e){upd(f.k,e.target.value);}} style={{padding:"6px 10px",fontSize:12,colorScheme:"light"}}/>
          </div>
        );})}
      </div>
    </div>}

    {/* ── POST-CLOSING / FUNDING TRACKER ── */}
    {(loan.stage==="Closed"||loan.stage==="Funded")&&<PostClosingTracker key={loan.id} loan={loan} upd={upd} $={$} changeStage={changeStage}/>}

    {/* ── Everything else: pull up when needed ── */}
    <div style={{marginTop:6}}>
      {/* Reimbursements owed to you (10/10/26) — every file, any stage; open
          by default while something is owed. The header chip lands here. */}
      <Section id="ld-sec-reimb" title="Reimbursements owed to you" color={owedNow>0?C.clayDeep:null}
        summary={owedNow>0?fmtMoney(owedNow)+" owed · "+owedCount+" item"+(owedCount===1?"":"s"):(liveItems(loan).length?"All reimbursed":"None — add one when you pay for a credit report, appraisal…")}
        open={isOpen("reimb")} onToggle={function(){toggleSec("reimb");}}>
        <ReimbursementsPanel key={loan.id} loan={loan} setLoans={setLoans} me={me} practice={practice} viewDoc={adminViewDoc}
          onWrite={function(){sOpenSec(function(p){return p.reimb===undefined?Object.assign({},p,{reimb:true}):p;});}}/>
      </Section>

      <Section id="ld-sec-details" title="Details" summary={[loan.stage,loan.type,loan.src,loan.estClose?"est. close "+loan.estClose:""].filter(Boolean).join(" · ")} open={isOpen("details")} onToggle={function(){toggleSec("details");}}>
        {counselingStage&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:8,background:idLocked?C.surfaceAlt:C.orangeBg,border:"1px solid "+(idLocked?C.border:C.orange+"50"),marginBottom:10,flexWrap:"wrap"}}>
          <span style={{fontSize:11,color:idLocked?C.textDim:C.orange,fontWeight:600,flex:1,minWidth:180}}>{idLocked?"🔒 Contact info & DOB are locked in — deal is at \""+loan.stage+"\".":"⚠ Unlocked for corrections — re-locks when you leave this page."}</span>
          <Btn small onClick={function(){sIdUnlocked(!idUnlocked);}} style={{fontSize:10,color:idLocked?C.accentLight:C.orange}}>{idLocked?"Unlock to fix a typo":"Re-lock"}</Btn>
        </div>}
        {idLocked?lr("Borrower Name",loan.b):er("Borrower Name","b",loan.b)}
        {idLocked?lr("Phone",loan.ph):er("Phone","ph",loan.ph)}
        {idLocked?lr("Email",loan.em):er("Email","em",loan.em)}
        {idLocked?lr("Property Address",loan.prop):er("Property Address","prop",loan.prop)}
        {idLocked?lr("Date of Birth",loan.dob?fmtDob(loan.dob)+(dobAge?"  ·  age "+dobAge:""):(dobAge?"age "+dobAge+" (no DOB on file)":""))
        :<div style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:"1px solid "+C.border}}>
          <span style={{fontSize:11,color:C.textDim,fontWeight:600,minWidth:140,textTransform:"uppercase",letterSpacing:.3}}>Date of Birth</span>
          <div style={{flex:1,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <Inp type="date" value={loan.dob||""} onChange={function(e){var v=e.target.value;upd("dob",v);upd("age",ageFromDobStr(v)||0);}} style={{padding:"7px 12px",fontSize:12,colorScheme:"light"}}/>
            <span style={{fontSize:12,color:dobAge?C.text:C.textDim,fontWeight:700,whiteSpace:"nowrap"}}>{dobAge?"Age "+dobAge:(loan.age?"Age "+loan.age+" on file — add DOB to verify":"—")}</span>
          </div>
        </div>}
        {er("Home Value","hv",loan.hv,true)}
        {er("Loan Balance","mb",loan.mb,true)}
        {er("Lead Source","src",loan.src,false,true,["Mailer","Referral","Web Lead","Seminar","TV/Radio","Other"].map(s=>({value:s,label:s})))}
        {loan.src==="Mailer"&&er("DM Campaign","dmCampaign",loan.dmCampaign||"",false,true,[{value:"",label:"— None —"},...(dmCampaigns||DM_CAMPAIGNS).map(v=>({value:v,label:v}))])}
        {er("Referral Partner","ref",loan.ref||"")}
        {/* Fallout stages are appended so a fallen-out file shows its real stage
            here (and can be moved back onto the pipeline) instead of the select
            silently snapping to the first option. changeStage keeps archived /
            reason in step with the stage (see above). */}
        {er("Stage","stage",loan.stage,false,true,STAGES.concat(FALLOUT_STAGES).map(function(s){return{value:s,label:s};}),changeStage)}
        {er("Working List","queue",loan.queue||"",false,true,[{value:"",label:"— Not on a working list —"},{value:"callin",label:"📞 Call-in — not yet spoken to"},{value:"numbers",label:"🧮 Spoken to — run the numbers"}])}
        {er("Loan Type","type",loan.type,false,true,[{value:"HECM",label:"HECM"},{value:"Jumbo",label:"Jumbo"},{value:HS2_LOAN_TYPE,label:HS2_LOAN_TYPE},{value:"FHA",label:"FHA"},{value:"Conventional",label:"Conventional"},{value:"VA",label:"VA"}])}
        {er("Est. Close Date","estClose",loan.estClose)}
        <div style={{display:"flex",justifyContent:"flex-end",marginTop:12}}>
          {/* A fallout file is archived BY its stage — "unarchiving" it alone
              would leave a fallout file counted as active. Revive instead. */}
          {isOut?<Btn small onClick={reviveToPipeline} style={{color:C.cyan}}>↩ Back to pipeline</Btn>
          :<Btn small onClick={function(){
            if(loan.archived){upd("archived",false);addLogEntry("File unarchived","system");}
            else{if(!window.confirm("Archive "+loan.id+" — "+loan.b+"?\n\nThe file will be hidden from the active pipeline but kept for reporting. You can unarchive it any time."))return;upd("archived",true);addLogEntry("File archived","system");}
          }} style={{color:loan.archived?C.cyan:C.textDim}}>{loan.archived?"🗂 Unarchive":"🗂 Archive File"}</Btn>}
        </div>
      </Section>

      <Section id="ld-sec-checklist" title="Submission Checklist" summary={deal.lender||"Generic lender list"} open={isOpen("checklist")} onToggle={function(){toggleSec("checklist");}}>
        <LenderChecklist loan={loan} deal={deal} bare/>
      </Section>

      <Section id="ld-sec-docs" title="Documents" color={pendingDocs?C.accent:null} summary={uploads.length?uploads.length+" uploaded"+(pendingDocs?" · "+pendingDocs+" to review":""):(loan.statementData?"statement info submitted":"none yet")} open={isOpen("docs")} onToggle={function(){toggleSec("docs");}}>
        {uploads.length===0?<div style={{padding:16,textAlign:"center",color:C.textDim,fontSize:12}}>{ownOnly?"No documents yet — add one below.":"No documents uploaded yet"+(acct?". Borrower has portal access.":" — create portal access first.")}</div>
        :<div>{uploads.map(function(doc,i){return(
          <div key={i} style={{padding:"10px 0",borderBottom:i<uploads.length-1?"1px solid "+C.border:"none"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:160}}>
                <div style={{fontSize:13,fontWeight:600}}>{doc.name}</div>
                <div style={{fontSize:11,color:C.textDim}}>{doc.date} • {doc.type||"Document"}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <Sel value={doc.status||"Received"} onChange={function(e){updateUploadStatus(i,e.target.value);}} options={[{value:"Received",label:"Received"},{value:"In Review",label:"In Review"},{value:"Complete",label:"Complete"}]}/>
                {(doc.data||doc.storagePath)&&<button onClick={function(){adminViewDoc(doc);}} style={{background:"none",border:"none",color:C.accent,cursor:"pointer",fontSize:11,fontWeight:600}}>View</button>}
                <Btn small onClick={function(){removeUpload(i);}} style={{color:C.red,fontSize:10}}>Remove</Btn>
              </div>
            </div>
            {doc.aiReview&&<div style={{marginTop:6,padding:"8px 10px",background:C.surfaceAlt,borderRadius:6,border:"1px solid "+C.border}}>
              <div style={{fontSize:10,fontWeight:700,color:C.accent,marginBottom:4}}>AI Auto-Review {doc.reviewDate?" • "+doc.reviewDate:""}</div>
              <div style={{fontSize:11,color:C.text,lineHeight:1.6,whiteSpace:"pre-line"}}>{doc.aiReview}</div>
            </div>}
          </div>
        );})}
        </div>}

        {/* Max adds his own file's documents (no borrower portal on his files).
            Practice mode: nothing is uploaded. */}
        {ownOnly&&<div style={{marginTop:12,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <input ref={docInputRef} type="file" accept="application/pdf,image/*" data-doc-upload="1" style={{display:"none"}}
            onChange={function(e){var fl=e.target.files&&e.target.files[0];e.target.value="";if(fl)uploadDoc(fl);}}/>
          <Btn small primary onClick={function(){if(practice){sDocMsg("Practice mode — documents aren't uploaded. Turn it off in Settings to add one for real.");return;}if(docInputRef.current)docInputRef.current.click();}} disabled={docBusy}>{docBusy?"Adding…":"+ Add a document"}</Btn>
          <span style={{fontSize:11,color:C.textDim}}>PDF or photo — kept on this file.</span>
          {docMsg&&<span role="status" style={{fontSize:11.5,fontWeight:600,color:/^✓/.test(docMsg)?C.green:C.orange}}>{docMsg}</span>}
        </div>}

        {/* Statement Data (submitted by borrower) */}
        {loan.statementData&&<div style={{marginTop:14,padding:"12px 14px",background:C.surfaceAlt,borderRadius:8}}>
          <div style={{fontSize:11,fontWeight:700,color:C.accentLight,marginBottom:8}}>Statement Info (Submitted by Borrower)</div>
          {loan.statementData.photoUploadFailed&&!loan.statementData.statementPhotoUploaded&&<div style={{fontSize:12,fontWeight:700,color:C.red,background:C.redBg,border:"1px solid "+C.red+"40",borderRadius:6,padding:"7px 10px",marginBottom:8}}>Borrower's statement photo FAILED to upload — ask them to resend</div>}
          {[["Servicer",loan.statementData.servicer],["Balance/UPB",loan.statementData.balance],["Interest Rate",loan.statementData.rate],["Principal Limit",loan.statementData.principalLimit],["Available LOC",loan.statementData.loc],["Youngest DOB",loan.statementData.youngestDob],["Goals/Notes",loan.statementData.goals]].filter(function(r){return r[1];}).map(function(r){return(
            <div key={r[0]} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid "+C.border}}>
              <span style={{fontSize:11,color:C.textDim}}>{r[0]}</span>
              <span style={{fontSize:12,fontWeight:500,color:C.text,maxWidth:"60%",textAlign:"right"}}>{r[1]}</span>
            </div>
          );})}
          <div style={{fontSize:10,color:C.textDim,marginTop:6}}>Submitted: {loan.statementData.submittedDate||"Unknown"}</div>
        </div>}

        {/* Testimonial (submitted by borrower at funding) */}
        {loan.docs&&loan.docs.testimonial&&<div style={{marginTop:14,padding:"14px 16px",background:"linear-gradient(135deg,"+C.greenBg+","+C.surfaceAlt+")",border:"1px solid "+C.green+"40",borderRadius:8}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:C.green}}>⭐ Borrower Testimonial{loan.docs.testimonial.rating?" — "+"★".repeat(loan.docs.testimonial.rating):""}</div>
            <Btn small onClick={function(){try{navigator.clipboard.writeText('"'+loan.docs.testimonial.text+'" — '+loan.b);window.alert("Copied to clipboard");}catch(e){}}}>Copy</Btn>
          </div>
          <div style={{fontSize:13,color:C.text,lineHeight:1.7,fontStyle:"italic"}}>"{loan.docs.testimonial.text}"</div>
          <div style={{fontSize:10,color:C.textDim,marginTop:8}}>Submitted {loan.docs.testimonial.submittedDate||""} · saved for marketing use</div>
        </div>}
      </Section>

      {/* Borrower portal — Reece only (access.portal): never on Max's files. */}
      {access.portal&&<Section id="ld-sec-portal" title="Borrower Portal" color={acct?C.green:null} summary={acct?"Active · created "+acct.created+((loan.portalLogins||[]).length?" · "+(loan.portalLogins||[]).length+" logins":""):"No access yet"} open={isOpen("portal")} onToggle={function(){toggleSec("portal");}}>
        {/* Streamlined FA Toggle */}
        <div style={{padding:"10px 14px",background:C.surfaceAlt,borderRadius:8,marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
          <div><div style={{fontSize:12,fontWeight:700,color:C.text}}>Streamlined Financial Assessment</div><div style={{fontSize:10,color:C.textDim,marginTop:2}}>Removes income verification docs from borrower's required checklist</div></div>
          <button onClick={function(){upd("streamlinedFA",!loan.streamlinedFA);}} style={{width:44,height:24,borderRadius:12,border:"none",background:loan.streamlinedFA?C.green:C.borderLight,cursor:"pointer",position:"relative",transition:"background 0.2s",flexShrink:0}}>
            <div style={{width:18,height:18,borderRadius:"50%",background:C.white,position:"absolute",top:3,left:loan.streamlinedFA?23:3,transition:"left 0.2s"}}/>
          </button>
        </div>
        {acct?<div>
          <div style={{padding:"10px 14px",background:C.greenBg,borderRadius:8,marginBottom:12}}>
            <div style={{fontSize:11,color:C.green,fontWeight:600}}>Portal Active</div>
            {acct.passwordHash&&!acct.password?<div style={{fontSize:12,color:C.textMuted,marginTop:4,display:"flex",alignItems:"center",gap:6}}><span style={{color:C.green}}>🔒</span>Code stored hashed — admin cannot view it</div>
            :<div style={{fontSize:13,fontWeight:700,color:C.text,marginTop:4,fontFamily:"'Mulish',system-ui,sans-serif"}}>{acct.password}<span style={{fontSize:9,marginLeft:8,color:C.orange,fontFamily:"'Mulish',system-ui,sans-serif",fontWeight:600}}>LEGACY — revoke & recreate to secure</span></div>}
            <div style={{fontSize:10,color:C.textDim,marginTop:2}}>Created: {acct.created}</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <Btn small primary onClick={openAccessModal}>{Ic.doc} Send Access Email</Btn>
            <Btn small onClick={removePortalAccess} style={{color:C.red}}>Revoke Portal Access</Btn>
          </div>
          <div style={{fontSize:10,color:C.textDim,marginTop:6}}>Sending issues a fresh code (the old one is hashed and can't be re-shown).</div>

          {/* Portal Login History */}
          <div style={{marginTop:14,padding:"10px 14px",background:C.surfaceAlt,borderRadius:8}}>
            <div style={{fontSize:10,fontWeight:700,color:C.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:.5}}>Portal Login History</div>
            {(loan.portalLogins||[]).length===0?<div style={{fontSize:11,color:C.textDim}}>No logins recorded yet</div>
            :<div style={{maxHeight:140,overflowY:"auto"}}>
              {(loan.portalLogins||[]).slice().reverse().map(function(login,i){return(
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 0",borderBottom:i<(loan.portalLogins||[]).length-1?"1px solid "+C.border:"none"}}>
                  <span style={{fontSize:11,color:C.green}}>{Ic.chk} Logged in</span>
                  <span style={{fontSize:11,color:C.textDim,fontFamily:"'Mulish',system-ui,sans-serif"}}>{login.date} {login.time}</span>
                </div>
              );})}
            </div>}
          </div>
        </div>:<div>
          <p style={{fontSize:12,color:C.textDim,margin:"0 0 12px"}}>Give this borrower portal access to track progress and upload documents. The invite email includes a one-click login link, their access code, and the document checklist you choose.</p>
          <Btn primary small onClick={openAccessModal} style={{marginBottom:10}}>{Ic.lock} Create &amp; Send Access</Btn>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <Inp placeholder="Custom code (or auto-generate)" value={newCode} onChange={function(e){sNewCode(e.target.value);}} style={{padding:"8px 12px",fontSize:12}}/>
            <Btn small onClick={createPortalAccess}>Create code only</Btn>
          </div>
        </div>}
      </Section>}

      {(loan.scenarioHistory||[]).length>0&&<Section id="ld-sec-history" title="Scenario History" summary={loan.scenarioHistory.length+" run"+(loan.scenarioHistory.length===1?"":"s")} open={isOpen("history")} onToggle={function(){toggleSec("history");}}>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {loan.scenarioHistory.slice().reverse().map(function(s,i){
            // A deal entered by hand carries the lender's loan amount, not a
            // calculator net PL; a run priced on the lender's amount says so
            // and what the calculator had said — the history never reads a
            // typed figure as a calculator output.
            var lenderFigure=!s.manual&&Number(s.lenderAmount)>0;
            return <div key={i} data-history-entry={s.manual?"manual":lenderFigure?"lender":"calc"} style={{padding:"10px 14px",borderRadius:8,background:C.surfaceAlt,border:"1px solid "+C.border}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,marginBottom:6}}>
              <span style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span style={{fontSize:13,fontWeight:600,color:C.text}}>{s.product}</span>
                {s.manual&&<Badge label={MANUAL_DEAL_LABEL} color={C.purple} bg={C.purpleBg}/>}
                {lenderFigure&&<Badge label="Lender's amount" color={C.purple} bg={C.purpleBg}/>}
                {(s.dealType==="hs2"||s.dealKind==="hs2")&&<Badge label={HS2_LOAN_TYPE} color={C.navy} bg={C.navy+"14"}/>}
              </span>
              <span style={{fontSize:10,color:C.textDim}}>{s.date}</span>
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:12,fontSize:11,color:C.textMuted}}>
              <span>Lender: <strong style={{color:C.text}}>{s.lender}</strong></span>
              <span>Home: <strong style={{color:C.text}}>${(s.homeValue||s.appraisedValue||0).toLocaleString()}</strong></span>
              <span>{s.firstLien!=null?"First lien":"Balance"}: <strong style={{color:C.text}}>${(s.firstLien!=null?Number(s.firstLien)||0:(s.mortgageBalance||s.payoff||0)).toLocaleString()}</strong></span>
              {s.payOnly
                // HomeSafe Second mode (PL in, pay out): FOA's amount + the pay — no net / cash.
                ?<><span>HomeSafe Second (FOA's): <strong style={{color:C.text}}>${Math.round(Number(s.grossPL)||0).toLocaleString()}</strong></span>
                  <span>Your pay: <strong style={{color:C.gold}}>{payCents(s.pay)}</strong></span></>
                :<>{s.manual
                ?<span>Loan amount (by hand): <strong style={{color:C.text}}>${Math.round(Number(s.grossPL)||0).toLocaleString()}</strong></span>
                :<span>Net PL: <strong style={{color:C.text}}>${(s.netPL||0).toLocaleString()}</strong></span>}
              <span>Available: <strong style={{color:C.green}}>${(s.cashToBorrower||0).toLocaleString()}</strong></span></>}
            </div>
            {lenderFigure&&<div style={{fontSize:10.5,color:C.textMuted,marginTop:4}}>Lender figure <strong style={{color:C.text}}>${Math.round(Number(s.lenderAmount)).toLocaleString()}</strong>{Number(s.calcAmount)>0?" (calculator said $"+Math.round(Number(s.calcAmount)).toLocaleString()+")":""}</div>}
            {s.rate&&<div style={{fontSize:10,color:C.textDim,marginTop:4}}>{s.rate}</div>}
          </div>;})}
        </div>
      </Section>}
    </div>

    {/* One-time reveal of a freshly created portal code — kept outside the
        collapsed Portal section so it can never be hidden before it's saved. */}
    {justCreatedCode&&<div style={{position:"fixed",left:"50%",bottom:24,transform:"translateX(-50%)",zIndex:1100,width:"min(520px, calc(100% - 32px))",padding:"14px 16px",background:C.card,border:"1px solid "+C.orange+"80",borderRadius:12,boxShadow:"0 12px 40px rgba(6,26,48,0.3)"}}>
      <div style={{fontSize:10,color:C.orange,fontWeight:700,marginBottom:6,letterSpacing:.5}}>⚠ SAVE THIS PORTAL CODE NOW — it won't be shown again</div>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:18,fontWeight:700,color:C.text,letterSpacing:1,flex:1}}>{justCreatedCode}</span>
        <Btn small onClick={function(){try{navigator.clipboard.writeText(justCreatedCode);}catch(e){}}}>Copy</Btn>
        <Btn small primary onClick={function(){setJustCreatedCode("");}}>I saved it</Btn>
      </div>
    </div>}

    {/* ── Notes drawer ── */}
    {notesOpen&&<div onClick={closeNotes} style={{position:"fixed",inset:0,background:"rgba(6,26,48,0.35)",zIndex:1000}}>
      <div onClick={function(e){e.stopPropagation();}} style={{position:"absolute",top:0,right:0,bottom:0,width:"min(460px, 100%)",background:C.card,borderLeft:"1px solid "+C.border,boxShadow:"-12px 0 40px rgba(6,26,48,0.25)",display:"flex",flexDirection:"column"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"18px 20px",borderBottom:"1px solid "+C.border}}>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:C.text}}>Notes & Activity</div>
            <div style={{fontSize:11,color:C.textDim,marginTop:2}}>{loan.b} · {loan.id}</div>
          </div>
          <button onClick={closeNotes} style={{background:"none",border:"none",color:C.textDim,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{padding:"14px 20px",borderBottom:"1px solid "+C.border}}>
          <textarea ref={noteRef} placeholder="Add a note… saves automatically (Enter to add now, Shift+Enter for a new line)" value={newNote} onChange={function(e){var v=e.target.value;noteDraftRef.current=v;sNewNote(v);if(noteSaved)sNoteSaved(false);}} onBlur={addNote} onKeyDown={function(e){if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();addNote();}if(e.key==="Escape")closeNotes();}} style={{width:"100%",minHeight:70,maxHeight:160,padding:"10px 14px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:13,fontFamily:"'Mulish',system-ui,sans-serif",resize:"vertical",outline:"none",boxSizing:"border-box"}}/>
          <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:10,marginTop:8}}>
            <span style={{fontSize:11,color:noteSaved?C.green:C.textDim,fontWeight:noteSaved?700:400,marginRight:"auto"}}>{noteSaved?"✓ Saved to the file":newNote.trim()?"Saves when you click away or close":""}</span>
            <Btn primary small onClick={addNote} disabled={!newNote.trim()}>Add Note</Btn>
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"4px 20px 20px"}}>
          {log.length===0?<div style={{padding:16,textAlign:"center",color:C.textDim,fontSize:12}}>No activity yet.</div>
          :log.slice().reverse().map(function(entry,i){
            var typeC=entry.type==="stage"?C.purple:entry.type==="doc"?C.accent:entry.type==="system"?C.orange:C.textMuted;
            var typeLabel=entry.type==="stage"?"Stage Change":entry.type==="doc"?"Document":entry.type==="system"?"System":"Note";
            return(<div key={i} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:i<log.length-1?"1px solid "+C.border:"none"}}>
              <div style={{width:3,borderRadius:2,background:typeC,flexShrink:0}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                  <span style={{fontSize:9,fontWeight:700,color:typeC,background:typeC+"18",padding:"1px 6px",borderRadius:4,textTransform:"uppercase"}}>{typeLabel}</span>
                  <span style={{fontSize:10,color:C.textDim}}>{entry.date||""} {entry.time||""}</span>
                </div>
                <div style={{fontSize:13,color:C.text,lineHeight:1.5,wordBreak:"break-word"}}>{entry.text}</div>
              </div>
            </div>);
          })}
        </div>
      </div>
    </div>}

    {/* ── Fall-out modal ── */}
    {fallout&&<div onClick={leaveFallout} style={{position:"fixed",inset:0,background:"rgba(6,26,48,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={function(e){e.stopPropagation();}} style={{background:C.card,border:"1px solid "+C.border,borderRadius:16,width:520,maxWidth:"100%",maxHeight:"88vh",overflowY:"auto",padding:24,boxShadow:"0 18px 60px rgba(6,26,48,0.35)"}}>
        <h3 style={{margin:"0 0 4px",fontSize:17,fontWeight:800,color:C.text}}>Move {loan.b||"this file"} to Fallout</h3>
        <p style={{margin:"0 0 16px",fontSize:12,color:C.textMuted,lineHeight:1.5}}>Takes it off the active pipeline and onto the Fallout board. Every number and note stays on the file.</p>
        <div style={{display:"flex",gap:7,marginBottom:16,flexWrap:"wrap"}}>
          {FALLOUT_STAGES.map(function(st){
            var on=fallout.stage===st;var col=STC[st]||C.accent;
            return <button key={st} onClick={function(){askFallout(st);}} style={{flex:"1 1 30%",minWidth:110,padding:"10px 8px",borderRadius:9,cursor:"pointer",fontSize:11.5,fontWeight:800,fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+(on?col:C.border),background:on?col+"16":"transparent",color:on?col:C.textMuted}}>{st.replace("Fallout — ","")}</button>;
          })}
        </div>
        <label style={{display:"block",fontSize:10,fontWeight:800,color:C.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:.6}}>Why</label>
        <select value={fallout.reason} onChange={function(e){pickFalloutReason(e.target.value);}} style={{width:"100%",padding:"11px 14px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:14,fontFamily:"'Mulish',system-ui,sans-serif",marginBottom:12}}>
          {(FALLOUT_REASONS[fallout.stage]||[]).map(function(r){return <option key={r} value={r}>{r}</option>;})}
        </select>
        {/* Do not contact — auto-ticked on "asked to be removed from mailings".
            A file has no DNC flag of its own: it lands on the mail-list row
            this file came from — so it isn't offered on a login without the
            leads (Max). */}
        {access.leads&&<label style={{display:"flex",alignItems:"center",gap:9,marginBottom:fallout.dnc?6:14,fontSize:12.5,color:fallout.dnc?C.red:C.textMuted,cursor:"pointer",fontWeight:fallout.dnc?700:500}}>
          <input type="checkbox" checked={!!fallout.dnc} onChange={function(e){var v=e.target.checked;sFallout(function(f){return Object.assign({},f,{dnc:v});});}} style={{width:16,height:16,cursor:"pointer"}}/>
          Mark do not contact — drop them from every future mailing and list
        </label>}
        {access.leads&&fallout.dnc&&(function(){
          var lead=findLinkedLead();
          var msg,warn=false;
          if(!setMktgLeads){msg="The mailing list can't be updated from this screen — mark them DNC in Marketing too.";warn=true;}
          else if(lead)msg="Marks "+(lead.name||"their record")+" ("+lead.id+") do not contact on the mailing list.";
          else if(loan.sourceLeadId)msg="Will look up mailing-list record "+loan.sourceLeadId+" and mark it do not contact.";
          else{msg="No mailing-list record is linked to this file — if they're on a mail list, mark them DNC in Marketing.";warn=true;}
          return <div style={{fontSize:11.5,lineHeight:1.5,color:warn?C.orange:C.textMuted,fontWeight:warn?700:500,margin:"0 0 14px 25px"}}>{msg}</div>;
        })()}
        <label style={{display:"block",fontSize:10,fontWeight:800,color:C.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:.6}}>Note (optional)</label>
        <textarea value={fallout.note} onChange={function(e){var v=e.target.value;sFallout(function(f){return Object.assign({},f,{note:v});});}} placeholder="Goes on the file with today's date — kept as a note even if you back out." style={{width:"100%",minHeight:60,padding:"10px 14px",borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontSize:13,fontFamily:"'Mulish',system-ui,sans-serif",resize:"vertical",outline:"none",boxSizing:"border-box",marginBottom:12}}/>
        <label style={{display:"block",fontSize:10,fontWeight:800,color:C.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:.6}}>Call back on</label>
        <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:9}}>
          {[{d:30,l:"30 days"},{d:90,l:"90 days"},{d:180,l:"6 months"},{d:365,l:"1 year"}].map(function(o){
            var on=fallout.followUp===isoDatePlus(o.d);
            return <button key={o.d} onClick={function(){sFallout(function(f){return Object.assign({},f,{followUp:isoDatePlus(o.d)});});}} style={{padding:"5px 11px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+(on?C.orange:C.border),background:on?C.orangeBg:"transparent",color:on?C.orange:C.textMuted}}>{o.l}</button>;
          })}
          <button onClick={function(){sFallout(function(f){return Object.assign({},f,{followUp:""});});}} style={{padding:"5px 11px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",border:"1px solid "+C.border,background:"transparent",color:C.textDim}}>None</button>
        </div>
        <Inp type="date" value={fallout.followUp} onChange={function(e){var v=e.target.value;sFallout(function(f){return Object.assign({},f,{followUp:v});});}} style={{colorScheme:"light"}}/>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
          <Btn small onClick={leaveFallout} title="Close without moving it — a typed note is kept on the file">Cancel</Btn>
          <Btn small danger onClick={confirmFallout}>Move to {fallout.stage.replace("Fallout — ","")}</Btn>
        </div>
      </div>
    </div>}

    {/* ── Send Portal Access modal ── */}
    {showAccessModal&&accessDocReq&&(
      <div onClick={function(){if(!accessSending)sShowAccessModal(false);}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1000,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"24px 16px",overflowY:"auto"}}>
        <div onClick={function(e){e.stopPropagation();}} style={{background:C.card,border:"1px solid "+C.border,borderRadius:16,maxWidth:560,width:"100%",padding:24,marginTop:20,marginBottom:40}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
            <h3 style={{fontSize:17,fontWeight:800,color:C.text,margin:0}}>Send Portal Access</h3>
            <button onClick={function(){if(!accessSending)sShowAccessModal(false);}} style={{background:"none",border:"none",color:C.textDim,fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
          </div>
          <p style={{fontSize:12,color:C.textDim,margin:"0 0 16px"}}>Emails {loan.b||"the borrower"} a one-click login link, their access code, and the document checklist below. Re-sending issues a fresh code.</p>

          <label style={{display:"block",fontSize:11,fontWeight:700,color:C.textDim,marginBottom:5,textTransform:"uppercase",letterSpacing:.4}}>Borrower email</label>
          <Inp type="email" value={accessEmail} onChange={function(e){sAccessEmail(e.target.value);}} placeholder="name@example.com" style={{padding:"9px 12px",fontSize:13,marginBottom:16,width:"100%"}}/>

          {/* Application Docs */}
          <div style={{fontSize:11,fontWeight:800,letterSpacing:.5,textTransform:"uppercase",color:C.accent,margin:"0 0 8px"}}>Application Docs</div>
          <div onClick={function(){toggleAccessItem("app_docs");}} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",borderRadius:8,border:"1px solid "+(accessDocReq.application.on?C.accent+"55":C.border),background:accessDocReq.application.on?C.accentGlow:C.surfaceAlt,cursor:"pointer",marginBottom:8}}>
            <div style={{width:18,height:18,borderRadius:4,border:"2px solid "+(accessDocReq.application.on?C.accent:C.borderLight),background:accessDocReq.application.on?C.accent:"transparent",flexShrink:0,marginTop:1,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11}}>{accessDocReq.application.on?"✓":""}</div>
            <div><div style={{fontSize:13,fontWeight:700,color:C.text}}>{accessDocReq.application.label}</div><div style={{fontSize:11,color:C.textDim,marginTop:2,lineHeight:1.4}}>{accessDocReq.application.desc}</div></div>
          </div>
          {accessDocReq.application.on&&<div style={{margin:"0 0 16px 28px"}}>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:C.textDim,marginBottom:4,textTransform:"uppercase",letterSpacing:.4}}>Quantum signing link (optional)</label>
            <Inp value={accessDocReq.application.link} onChange={function(e){setQuantumLink(e.target.value);}} placeholder="https://… paste the Quantum e-sign link" style={{padding:"8px 12px",fontSize:12,width:"100%"}}/>
          </div>}

          {/* Supporting Docs */}
          <div style={{fontSize:11,fontWeight:800,letterSpacing:.5,textTransform:"uppercase",color:C.accent,margin:"6px 0 8px"}}>Supporting Docs</div>
          {accessDocReq.supporting.map(function(item){return(
            <div key={item.id} onClick={function(){toggleAccessItem(item.id);}} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",borderRadius:8,border:"1px solid "+(item.on?C.accent+"55":C.border),background:item.on?C.accentGlow:C.surfaceAlt,cursor:"pointer",marginBottom:8}}>
              <div style={{width:18,height:18,borderRadius:4,border:"2px solid "+(item.on?C.accent:C.borderLight),background:item.on?C.accent:"transparent",flexShrink:0,marginTop:1,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:11}}>{item.on?"✓":""}</div>
              <div><div style={{fontSize:13,fontWeight:700,color:C.text}}>{item.label}</div><div style={{fontSize:11,color:C.textDim,marginTop:2,lineHeight:1.4}}>{item.desc}{item.link?<span style={{color:C.accent}}> · link included</span>:null}</div></div>
            </div>
          );})}

          {/* Custom items */}
          {accessCustom.map(function(c,i){return(
            <div key={"c"+i} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:8,border:"1px solid "+C.accent+"55",background:C.accentGlow,marginBottom:8}}>
              <span style={{fontSize:13,fontWeight:600,color:C.text,flex:1}}>{c}</span>
              <button onClick={function(){removeAccessCustom(i);}} style={{background:"none",border:"none",color:C.textDim,fontSize:18,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
          );})}
          <div style={{display:"flex",gap:8,marginTop:4}}>
            <Inp value={accessNewCustom} onChange={function(e){sAccessNewCustom(e.target.value);}} onKeyDown={function(e){if(e.key==="Enter"){e.preventDefault();addAccessCustom();}}} placeholder="Add another item…" style={{padding:"8px 12px",fontSize:12,flex:1}}/>
            <Btn small onClick={addAccessCustom}>Add</Btn>
          </div>

          <label style={{display:"block",fontSize:11,fontWeight:700,color:C.textDim,margin:"16px 0 5px",textTransform:"uppercase",letterSpacing:.4}}>Personal note (optional)</label>
          <textarea value={accessNote} onChange={function(e){sAccessNote(e.target.value);}} placeholder="A short note shown in the email…" rows={3} style={{width:"100%",padding:"9px 12px",fontSize:13,borderRadius:8,border:"1px solid "+C.border,background:C.surfaceAlt,color:C.text,fontFamily:"'Mulish',system-ui,sans-serif",resize:"vertical",boxSizing:"border-box"}}/>

          {accessMsg&&<div style={{marginTop:12,padding:"10px 12px",background:C.orangeBg,border:"1px solid "+C.orange+"40",borderRadius:8,fontSize:12,color:C.orange,lineHeight:1.5}}>{accessMsg}</div>}

          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18,flexWrap:"wrap"}}>
            <Btn small onClick={function(){if(!accessSending)sShowAccessModal(false);}}>Cancel</Btn>
            <Btn small onClick={function(){submitAccess(false);}} disabled={accessSending}>Create without emailing</Btn>
            <Btn primary small onClick={function(){submitAccess(true);}} disabled={accessSending}>{accessSending?"Sending…":"Create & Send Email"}</Btn>
          </div>
        </div>
      </div>
    )}
  </div>);
}

export { PostClosingTracker, LenderChecklist, LoanDetail };
